import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback
} from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addWsListener,
  connectWs,
  closeWs,
  sendWs,
  isWsOpen,
  getWsDebugSnapshot,
  WS_ACTIVE_CHAT_ID_KEY,
  normalizeWsId
} from "../services/wsClient";

/** Below this width we use WhatsApp-style list → thread (full-screen panes). */
const MOBILE_CHAT_MQ = "(max-width: 991.98px)";

/** Distance from bottom (px) to still treat the list as "at bottom" for auto-scroll. */
const CHAT_STICK_BOTTOM_THRESHOLD_PX = 96;

/** Pinned group rows — not real one-to-one chats. */
const GROUP_OWNERS = "OWNERS";
const GROUP_TENANTS = "TENANTS";
const GROUP_EXECUTIVES = "EXECUTIVES";

const GROUP_META = {
  [GROUP_OWNERS]: {
    id: "__group_owners__",
    name: "Owners",
    subtitle: "Group message — all owners"
  },
  [GROUP_TENANTS]: {
    id: "__group_tenants__",
    name: "Tenants",
    subtitle: "Group message — all tenants"
  },
  [GROUP_EXECUTIVES]: {
    id: "__group_executives__",
    name: "Executive members",
    subtitle: "Group message — active executive members"
  }
};

const DELETED_GROUP_MESSAGE_TEXT = "Admin deleted this message";

function isUserDeletedMessage(message) {
  if (!message) return false;
  const status = String(message.status || "").trim().toLowerCase();
  const deletedBy = String(message.deletedBy || "").trim().toLowerCase();
  return status === "inactive" && deletedBy === "user";
}

function isDeletedGroupMessage(message) {
  if (!message || isUserDeletedMessage(message)) return false;
  const status = String(message.status || "").trim().toLowerCase();
  const deletedBy = String(message.deletedBy || "").trim().toLowerCase();
  if (status === "inactive" && deletedBy === "admin") return true;
  const type = String(message.messageType || "").toLowerCase();
  return Boolean(
    message.isDeleted ||
      message.deleted ||
      type === "deleted" ||
      String(message.content || "").trim() === DELETED_GROUP_MESSAGE_TEXT
  );
}

function asDeletedGroupMessage(message) {
  return {
    ...(message || {}),
    content: DELETED_GROUP_MESSAGE_TEXT,
    messageType: "deleted",
    isDeleted: true,
    status: "inactive",
    deletedBy: "admin"
  };
}

function visibleThreadMessages(list) {
  return (Array.isArray(list) ? list : [])
    .filter((item) => !isUserDeletedMessage(item))
    .map((item) => (isDeletedGroupMessage(item) ? asDeletedGroupMessage(item) : item));
}

function messageDocId(message) {
  const raw = message?._id || message?.id || message?.messageId;
  if (raw && typeof raw === "object") {
    return String(raw._id || raw.id || raw.$oid || raw.oid || "").trim();
  }
  return raw != null ? String(raw).trim() : "";
}

function groupNameOf(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim().toUpperCase();
  if (typeof value === "object") {
    return String(value.name || value.group || value.type || value.code || "")
      .trim()
      .toUpperCase();
  }
  return String(value).trim().toUpperCase();
}

function sameMessageId(item, messageId) {
  const id = String(messageId || "").trim();
  if (!id) return false;
  return [item?._id, item?.id, item?.messageId].some((value) => messageDocId({ _id: value }) === id);
}

function deletedGroupNotice(messageId, source) {
  const createdAt = source?.createdAt || source?.deletedAt || new Date().toISOString();
  return asDeletedGroupMessage({
    _id: messageId,
    createdAt,
    senderId: source?.senderId || { name: "Admin" },
    group: source?.group
  });
}

const DIRECTORY_ROLE_LABEL = {
  ADMIN: "Admin",
  SUPERADMIN: "Admin",
  OWNER: "Owner",
  TENANT: "Tenant",
  EXECUTIVE: "Executive member"
};

function normalizeDirectoryRole(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");
}

function pickApiList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== "object") return [];
  const keys = ["users", "chats", "tenants", "members", "communities", "community", "list", "result"];
  for (let i = 0; i < keys.length; i += 1) {
    if (Array.isArray(data[keys[i]])) return data[keys[i]];
  }
  if (data.data && data.data !== data) return pickApiList(data.data);
  return [];
}

function personIdOf(row) {
  if (!row || typeof row !== "object") return "";
  const nested = row.user || row.tenant || row.account;
  const candidates = [
    row.userId,
    row.userid,
    nested && typeof nested === "object" ? nested.userId : "",
    nested && typeof nested === "object" ? nested.userid : "",
    nested && typeof nested === "object" ? nested._id : "",
    row.tenantId,
    row.memberUserId,
    row._id,
    row.id
  ];
  const found = candidates.find((value) => value != null && String(value).trim() !== "");
  return found ? String(found).trim() : "";
}

function displayEmail(value) {
  const email = String(value || "").trim();
  return email.includes("@") ? email : "";
}

function messageSenderName(message) {
  const sender = message?.senderId;
  if (sender && typeof sender === "object") {
    return String(sender.name || sender.fullName || sender.email || "").trim();
  }
  return "";
}

const SENDER_NAME_COLORS = ["#e11d48", "#b45309", "#7c3aed", "#0f766e", "#1d4ed8", "#be185d"];

function senderNameColor(senderId) {
  const text = String(senderId || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash += text.charCodeAt(i);
  return SENDER_NAME_COLORS[hash % SENDER_NAME_COLORS.length];
}

function isActiveExecutiveMember(member) {
  const status = String(member?.executiveStatus || "active")
    .trim()
    .toLowerCase();
  return status === "active";
}

function isNotParticipantError(value) {
  return /not a participant/i.test(String(value || ""));
}

/** True when participants list is missing (unknown) or includes self. */
function chatIncludesSelf(chat, selfId) {
  if (!chat || !selfId) return true;
  const participants = Array.isArray(chat.participants) ? chat.participants : [];
  if (!participants.length) return true;
  return participants.some((person) => {
    const nested = person?.userId;
    const pid =
      (nested && typeof nested === "object" ? nested._id || nested.id : nested) ||
      person?._id ||
      person?.id ||
      person?.userid;
    return pid != null && String(pid) === String(selfId);
  });
}

function peerUserIdOfChat(chat) {
  if (!chat) return "";
  return String(
    chat?.otherParticipant?._id ||
      chat?.otherParticipant?.id ||
      chat?.otherParticipant?.userId ||
      chat?.userId ||
      chat?.participantId ||
      ""
  ).trim();
}

function participantUserIds(chat) {
  const participants = Array.isArray(chat?.participants) ? chat.participants : [];
  return participants
    .map((person) => {
      const nested = person?.userId;
      const pid =
        (nested && typeof nested === "object" ? nested._id || nested.id : nested) ||
        person?._id ||
        person?.id ||
        person?.userid;
      return pid != null ? String(pid).trim() : "";
    })
    .filter(Boolean);
}

/** 1:1 chat must include both users when the API returns participants. */
function isDirectChatBetween(chat, selfId, peerId) {
  const ids = participantUserIds(chat);
  if (!ids.length) return true;
  const set = new Set(ids);
  return set.has(String(selfId)) && set.has(String(peerId));
}

function withChatPeer(chat, selfId) {
  if (!chat) return chat;
  if (chat.otherParticipant?._id || chat.otherParticipant?.id || chat.otherParticipant?.name) {
    return chat;
  }
  const participants = Array.isArray(chat.participants) ? chat.participants : [];
  const other = participants.find((person) => {
    const nested = person?.userId;
    const pid =
      (nested && typeof nested === "object" ? nested._id || nested.id : nested) ||
      person?._id ||
      person?.id;
    return pid && String(pid) !== String(selfId || "");
  });
  if (!other) return chat;
  const user = other.userId && typeof other.userId === "object" ? other.userId : other;
  const id = String(user._id || user.id || other.userId || other._id || "").trim();
  if (!id) return chat;
  const role = normalizeDirectoryRole(user.role || other.role || chat.directoryRole);
  return {
    ...chat,
    otherParticipant: {
      _id: id,
      id,
      name: user.name || other.name || user.email || "User",
      email: user.email || other.email || ""
    },
    userId: id,
    participantId: id,
    directoryRole: role || chat.directoryRole
  };
}

function contactFromUser(row, directoryRole) {
  const id = personIdOf(row);
  if (!id) return null;
  const role = directoryRole || normalizeDirectoryRole(row?.role || row?.userType);
  const name = String(row?.name || row?.fullName || row?.email || "User").trim();
  const email = String(row?.email || "").trim();
  return {
    _id: null,
    chatId: null,
    lastMessage: null,
    unreadForMe: typeof row?.unreadForMe === "number" ? row.unreadForMe : 0,
    otherParticipant: { _id: id, id, name, email, role },
    userId: id,
    participantId: id,
    directoryRole: role
  };
}

function visibleGroupTypes({ isAdmin, isOwner, isTenant, isActiveExecutive }) {
  const types = [];
  if (isAdmin || isActiveExecutive || isOwner) types.push(GROUP_OWNERS);
  if (isAdmin || isActiveExecutive || isTenant) types.push(GROUP_TENANTS);
  if (isAdmin || isActiveExecutive) types.push(GROUP_EXECUTIVES);
  return types;
}

function canPostToGroup(groupType, flags) {
  if (groupType === GROUP_OWNERS || groupType === GROUP_TENANTS) {
    return flags.isAdmin || flags.isActiveExecutive;
  }
  if (groupType === GROUP_EXECUTIVES) return flags.isAdmin;
  return false;
}

const MAX_CHAT_ATTACHMENT_BYTES = 15 * 1024 * 1024;
const MAX_PENDING_ATTACHMENTS = 8;

/** Chat document id for list rows (`chatId` or legacy `_id`). */
function sidebarChatDocId(chat) {
  const cid = chat?.chatId ?? chat?._id ?? null;
  return cid != null && cid !== "" ? String(cid) : "";
}

/** Counterpart user id — admin tenant rows and 1:1 chats. */
function sidebarParticipantKey(chat) {
  if (!chat || chat.isGroup || chat.groupType || chat.isBroadcastAllTenants) return null;
  const p = chat.otherParticipant;
  const id =
    p?._id ?? p?.id ?? chat?.userId ?? chat?.participantId ?? null;
  return id != null && id !== "" ? String(id) : null;
}

/**
 * One row per conversation: merge duplicate Mongo ids and drop orphan "no chat yet" rows
 * when the same counterpart already has a chat id row.
 */
function dedupeSidebarChats(prevChats) {
  if (!Array.isArray(prevChats) || prevChats.length < 2) return prevChats;

  const withId = prevChats.filter((c) => sidebarChatDocId(c));
  const peerOfRowWithId = new Set();
  const uniqByCid = new Map();
  for (const c of withId) {
    const cid = sidebarChatDocId(c);
    const existing = uniqByCid.get(cid);
    uniqByCid.set(
      cid,
      existing
        ? {
            ...existing,
            ...c,
            otherParticipant: c.otherParticipant || existing.otherParticipant
          }
        : c
    );
    const p = sidebarParticipantKey(c);
    if (p) peerOfRowWithId.add(p);
  }

  const orderCids = [];
  for (const c of prevChats) {
    const cid = sidebarChatDocId(c);
    if (cid && !orderCids.includes(cid)) orderCids.push(cid);
  }
  const part1 = orderCids.map((id) => uniqByCid.get(id)).filter(Boolean);

  const part2 = [];
  const seenOrphanPeers = new Set();
  for (const c of prevChats) {
    if (sidebarChatDocId(c)) continue;
    const p = sidebarParticipantKey(c);
    if (!p) continue;
    if (peerOfRowWithId.has(p)) continue;
    if (seenOrphanPeers.has(p)) continue;
    seenOrphanPeers.add(p);
    part2.push(c);
  }

  return [...part1, ...part2];
}

/** Upsert chat into admin/tenant sidebar list without leaving duplicate counterpart rows. */
function mergeNormalizedChatIntoList(prevChats, normalized) {
  const prev = prevChats || [];
  const cid = sidebarChatDocId(normalized);
  const peer = sidebarParticipantKey(normalized);
  let idx = cid ? prev.findIndex((c) => sidebarChatDocId(c) === cid) : -1;
  if (idx === -1 && peer) {
    idx = prev.findIndex((c) => sidebarParticipantKey(c) === peer);
  }
  const next =
    idx === -1
      ? [{ ...normalized }, ...prev]
      : prev.map((c, i) =>
          i === idx
            ? {
                ...c,
                ...normalized,
                otherParticipant:
                  normalized.otherParticipant || c.otherParticipant
              }
            : c
        );
  return dedupeSidebarChats(next);
}

/** Milliseconds for sorting the sidebar (newest activity first). */
function conversationActivityMs(chat) {
  if (!chat) return 0;
  const lm = chat.lastMessage;
  const raw =
    lm?.timestamp ??
    lm?.createdAt ??
    lm?.updatedAt ??
    chat?.updatedAt ??
    chat?.lastActivityAt ??
    "";
  if (raw === "" || raw == null) return 0;
  const t = typeof raw === "number" ? raw : Date.parse(String(raw));
  return Number.isFinite(t) ? t : 0;
}

function conversationSortTieBreakName(a, b) {
  const ea = String(
    sidebarParticipantKey(a) || a?.otherParticipant?.email || a?.otherParticipant?.name || ""
  );
  const eb = String(
    sidebarParticipantKey(b) || b?.otherParticipant?.email || b?.otherParticipant?.name || ""
  );
  return ea.localeCompare(eb);
}

/**
 * Sidebar list: apply incoming message preview, unread bump, bump sort key.
 * Used from Communication WS handler and from Header `adukkumadi:chatSidebarSync` (Dashboard + other routes).
 */
function patchChatsStateForIncomingMessage(
  prev,
  chatId,
  message,
  { viewerUserId, openChatId, getChatId, getUnreadForMe }
) {
  if (!chatId || !message || !Array.isArray(prev)) return prev;

  const sameChat = String(openChatId || "") === String(chatId || "");
  const senderRaw =
    message?.senderId?._id ?? message?.senderId ?? message?.sender ?? null;
  const normalizedSender =
    senderRaw != null && senderRaw !== ""
      ? normalizeWsId(String(senderRaw))
      : "";
  const normalizedUser = normalizeWsId(String(viewerUserId || ""));
  const fromSelf =
    Boolean(normalizedSender && normalizedUser) &&
    normalizedSender === normalizedUser;

  return prev.map((chat) => {
    const id = getChatId(chat);
    if (String(id) !== String(chatId)) return chat;

    let unreadBase = Number(getUnreadForMe(chat));
    if (!Number.isFinite(unreadBase) || unreadBase < 0) unreadBase = 0;
    let unreadForMe = unreadBase;
    if (sameChat) {
      unreadForMe = 0;
    } else if (!fromSelf) {
      unreadForMe = unreadBase + 1;
    }

    const ts =
      message.createdAt || message.updatedAt || new Date().toISOString();

    return {
      ...chat,
      lastMessage: {
        content: message.content,
        senderId: message.senderId?._id || message.senderId,
        timestamp: ts,
        messageType: message.messageType,
        fileName: message.fileName
      },
      unreadForMe,
      ...(typeof chat.unreadCount === "number"
        ? { unreadCount: unreadForMe }
        : {})
    };
  });
}

function isApiSuccess(data) {
  return data?.success === true || data?.status === "success";
}

/** Delete API uses status "inactive" for the message, not status "success" for the call. */
function isGroupDeleteSaved(data) {
  if (!data || typeof data !== "object") return false;
  const status = String(data.status || "").trim().toLowerCase();
  if (data.success === false || status === "error" || status === "failed") return false;
  if (isApiSuccess(data)) return true;
  const deletedBy = String(data.deletedBy || "").trim().toLowerCase();
  const content = String(data.content || "").trim();
  return (
    status === "inactive" ||
    deletedBy === "admin" ||
    content === DELETED_GROUP_MESSAGE_TEXT
  );
}

function getMessageDisplayUrl(message) {
  const m = message || {};
  const candidates = [
    m.attachmentDownloadUrl,
    m.attachmentUrl,
    m.fileUrl,
    m.url,
    m.mediaUrl
  ];
  for (let i = 0; i < candidates.length; i++) {
    const u = candidates[i];
    if (typeof u === "string" && u.trim()) return u.trim();
  }
  if (Array.isArray(m.attachments)) {
    const a = m.attachments[0];
    if (a?.url) return a.url;
    if (a?.downloadUrl) return a.downloadUrl;
  }
  return null;
}

/**
 * chat.getAttachmentUploadUrl → { success, data: { uploadUrl, key, contentType, contentTypeSigned } }
 */
function parseChatPresignResponse(raw) {
  const d = raw || {};
  if (!isApiSuccess(d)) return null;
  const inner = d.data && typeof d.data === "object" ? d.data : null;
  if (!inner?.uploadUrl || !inner?.key) return null;
  return {
    uploadUrl: inner.uploadUrl,
    key: inner.key,
    contentType: inner.contentType,
    contentTypeSigned: inner.contentTypeSigned === true
  };
}

async function uploadChatFileViaPresign(apiBaseUrl, userId, chatId, file) {
  try {
    const presignRes = await axios.post(apiBaseUrl, {
      action: "chat.getAttachmentUploadUrl",
      userid: userId,
      chatId,
      fileName: file.name,
      contentType: file.type || "application/octet-stream"
    });
    const meta = parseChatPresignResponse(presignRes?.data);
    if (!meta) {
      const err =
        presignRes?.data?.error ||
        presignRes?.data?.message ||
        "Could not get upload URL";
      return { error: err };
    }
    const headers = {};
    if (meta.contentTypeSigned && meta.contentType) {
      headers["Content-Type"] = meta.contentType;
    }
    const putRes = await fetch(meta.uploadUrl, {
      method: "PUT",
      headers,
      body: file
    });
    if (!putRes.ok) {
      const errText = await putRes.text().catch(() => "");
      return {
        error: `Upload failed (${putRes.status}). ${errText ? errText.slice(0, 160) : ""}`.trim()
      };
    }
    return { s3Key: meta.key };
  } catch (e) {
    const msg =
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.message ||
      "Upload failed";
    return { error: msg };
  }
}

const Communication = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;
  const location = useLocation();
  const navigate = useNavigate();

  const focusFromRouteRef = useRef({ chatId: null, participantId: null });
  try {
    const qp = new URLSearchParams(location.search);
    focusFromRouteRef.current = {
      chatId: location.state?.focusChatId || qp.get("chatId") || null,
      participantId:
        location.state?.focusParticipantId ||
        qp.get("participantId") ||
        qp.get("userId") ||
        null
    };
  } catch {
    focusFromRouteRef.current = { chatId: null, participantId: null };
  }

  const userId = useMemo(
    () =>
      normalizeWsId(
        localStorage.getItem("userid") ||
          localStorage.getItem("userId") ||
          localStorage.getItem("id") ||
          ""
      ),
    []
  );
  const userRole = useMemo(
    () => (localStorage.getItem("role") || "").toUpperCase(),
    []
  );
  const isAdminCapable = userRole === "ADMIN" || userRole === "SUPERADMIN";

  const wsIdentity = useMemo(() => {
    return {
      userId,
      userType: userRole || "user"
    };
  }, [userId, userRole]);

  // Socket-only mode: keep minimal local state + rely on backend WS events
  const pendingCreateChatTargetRef = useRef(null);
  const pendingSendAfterChatCreateRef = useRef(null);
  // Guard against double-send on rapid Enter/click while HTTP is in flight
  const isSendingRef = useRef(false);

  // State management
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [sendingAttachments, setSendingAttachments] = useState(false);
  const imageAttachmentInputRef = useRef(null);
  const messageTextareaRef = useRef(null);
  const fileAttachmentInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [directoryPeople, setDirectoryPeople] = useState([]);
  const [peoplePickerOpen, setPeoplePickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [wsConnected, setWsConnected] = useState(false);
  const [showDebug] = useState(
    () =>
      typeof window !== "undefined" && localStorage.getItem("wsDebug") === "1"
  );
  const [openImageMenuMessageId, setOpenImageMenuMessageId] = useState(null);
  const [isMobileChat, setIsMobileChat] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_CHAT_MQ).matches : false
  );
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [isActiveExecutive, setIsActiveExecutive] = useState(false);
  const [executiveSenderIds, setExecutiveSenderIds] = useState(() => new Set());
  const [messageMenuId, setMessageMenuId] = useState(null);
  const [groupSummaries, setGroupSummaries] = useState({});

  // Pagination
  const [, setChatPage] = useState(1);
  const [chatLimit] = useState(20);
  const [messageLimit] = useState(50);
  const [hasMoreChats, setHasMoreChats] = useState(false);

  // Prevent full-page scroll on Communication screen (desktop)
  useEffect(() => {
    document.body.classList.add("communication-view");
    return () => {
      document.body.classList.remove("communication-view");
    };
  }, []);

  // Reset textarea height when message is cleared after send
  useEffect(() => {
    if (newMessage === "" && messageTextareaRef.current) {
      messageTextareaRef.current.style.height = "auto";
      messageTextareaRef.current.style.overflowY = "hidden";
    }
  }, [newMessage]);

  useEffect(() => {
    if (!openImageMenuMessageId) return;
    const onPointerDown = (e) => {
      if (e.target.closest?.(".chat-msg-image-menu-host")) return;
      setOpenImageMenuMessageId(null);
    };
    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [openImageMenuMessageId]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_CHAT_MQ);
    const onChange = () => {
      setIsMobileChat(mq.matches);
      if (!mq.matches) setMobileThreadOpen(false);
    };
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!selectedChat) setMobileThreadOpen(false);
  }, [selectedChat]);

  // Refs
  const messagesScrollRef = useRef(null);
  /** When true, keep the message list pinned to the bottom as content changes. */
  const stickToBottomRef = useRef(true);
  const typingTimeoutRef = useRef(null);

  const updateStickToBottomFromScroll = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = dist <= CHAT_STICK_BOTTOM_THRESHOLD_PX;
  }, []);

  useLayoutEffect(() => {
    if (messagesLoading) return;
    const el = messagesScrollRef.current;
    if (!el || messages.length === 0) return;
    if (!stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
    stickToBottomRef.current = true;
  }, [messages, messagesLoading]);

  useEffect(() => {
    const outer = messagesScrollRef.current;
    if (!outer || messages.length === 0 || messagesLoading) return;
    const inner = outer.querySelector(".messages-container");
    if (!inner) return;
    const ro = new ResizeObserver(() => {
      if (!stickToBottomRef.current) return;
      outer.scrollTop = outer.scrollHeight;
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [messages.length, messagesLoading, selectedChat]);

  const ensureWsConnected = useCallback(async () => {
    if (!userId || !WS_URL) return false;
    if (isWsOpen()) return true;
    try {
      await connectWs({ baseUrl: WS_URL, userId, userType: userRole || "user" });
      setWsConnected(true);
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[Communication] ensureWsConnected failed:", e);
      setWsConnected(false);
      return false;
    }
  }, [WS_URL, userId, userRole]);

  /** Reconnect: close singleton then open again (same as Header’s connectWs). */
  const reconnectWs = useCallback(async () => {
    if (!userId || !WS_URL) return false;
    if (localStorage.getItem("wsDebug") === "1") {
      // eslint-disable-next-line no-console
      console.log("[Communication] reconnectWs: closing shared socket, then reconnecting");
    }
    closeWs();
    setWsConnected(false);
    return ensureWsConnected();
  }, [userId, WS_URL, ensureWsConnected]);

  const getChatId = useCallback((chat) => {
    if (!chat || chat.isGroup || chat.groupType || chat.isBroadcastAllTenants) return null;
    return chat.chatId || chat._id || null;
  }, []);

  const getUnreadForMe = useCallback(
    (chat) => {
      if (!chat) return 0;
      if (typeof chat.unreadForMe === "number") return chat.unreadForMe;

      // Backward compatibility if unreadCount arrives as map/object/number
      if (typeof chat.unreadCount === "number") return chat.unreadCount;

      if (chat.unreadCount && typeof chat.unreadCount === "object") {
        return Number(chat.unreadCount[userId] || 0);
      }

      return 0;
    },
    [userId]
  );

  const isConversationSelected = useCallback(
    (chat) => {
      const selected = selectedChat;
      if (!chat || !selected) return false;
      const rowGroup = chat.groupType || (chat.isBroadcastAllTenants ? GROUP_TENANTS : "");
      const selectedGroup = selected.groupType || (selected.isBroadcastAllTenants ? GROUP_TENANTS : "");
      if (!!rowGroup !== !!selectedGroup) return false;
      if (rowGroup && selectedGroup) return rowGroup === selectedGroup;

      const rowChatId = getChatId(chat);
      const selChatId = getChatId(selected);
      // When open chat has a document id, only that row highlights (avoids double-active dupes).
      if (selChatId) {
        return Boolean(rowChatId) && String(rowChatId) === String(selChatId);
      }

      const rowP = sidebarParticipantKey(chat);
      const selP = sidebarParticipantKey(selected);
      return Boolean(rowP && selP && rowP === selP);
    },
    [selectedChat, getChatId]
  );

  // Keep latest selected chat available for socket handlers (avoid stale closures)
  const selectedChatRef = useRef(null);
  const messagesRef = useRef([]);
  const chatsRef = useRef([]);
  const deletedGroupMessageIdsRef = useRef(new Set());
  selectedChatRef.current = selectedChat;
  messagesRef.current = messages;
  chatsRef.current = chats;

  /** Dedup Header + WS applying the same message id twice (sidebar unread / preview). */
  const sidebarSyncMsgDedupRef = useRef(new Set());

  const applyIncomingChatToSidebar = useCallback(
    (chatId, message) => {
      if (!chatId || !message) return;
      const mid = message?._id || message?.id;
      if (mid) {
        const key = String(mid);
        if (sidebarSyncMsgDedupRef.current.has(key)) return;
        sidebarSyncMsgDedupRef.current.add(key);
        if (sidebarSyncMsgDedupRef.current.size > 400) {
          sidebarSyncMsgDedupRef.current.clear();
        }
      }
      setChats((prev) =>
        patchChatsStateForIncomingMessage(prev, chatId, message, {
          viewerUserId: userId,
          openChatId: getChatId(selectedChatRef.current),
          getChatId,
          getUnreadForMe
        })
      );
    },
    [userId, getChatId, getUnreadForMe]
  );

  // Header receives chat WS / poll on every route; mirror into sidebar and open thread.
  useEffect(() => {
    const onSidebarSync = (ev) => {
      const { chatId, message, chat, appendToThread = true } = ev?.detail || {};
      if (!chatId || !message) return;
      const content = String(message.content || message.fileName || "").trim();
      if (!content) return;

      // Skip thread-append for messages sent by the current user — finalizeSentMessage
      // already optimistically appended them. Letting the Header echo re-append causes duplicates.
      const senderRaw =
        message?.senderId?._id ?? message?.senderId ?? message?.sender ?? null;
      const normalizedSender =
        senderRaw != null && senderRaw !== ""
          ? normalizeWsId(String(senderRaw))
          : "";
      const normalizedSelf = normalizeWsId(String(userId || ""));
      const fromSelf =
        Boolean(normalizedSender && normalizedSelf) &&
        normalizedSender === normalizedSelf;

      setChats((prev) => {
        const exists = prev.some((row) => String(getChatId(row) || "") === String(chatId));
        // Do not seed chats the current user is not a participant of (avoids admin seeing
        // executive↔owner/tenant threads and then hitting "not a participant").
        if (!exists && chat && chatIncludesSelf(chat, userId)) {
          const other =
            chat.otherParticipant ||
            chat.participant ||
            message?.senderId ||
            { name: "User" };
          const seeded = {
            ...chat,
            _id: chatId,
            chatId,
            otherParticipant:
              other && typeof other === "object"
                ? other
                : { _id: other, name: "User" },
            lastMessage: {
              content: message.content,
              senderId: message.senderId?._id || message.senderId,
              timestamp: message.createdAt || new Date().toISOString(),
              messageType: message.messageType || "text",
              fileName: message.fileName
            },
            unreadForMe: 1
          };
          return dedupeSidebarChats([seeded, ...prev]);
        }
        return prev;
      });
      applyIncomingChatToSidebar(chatId, message);

      if (!appendToThread) return;
      if (fromSelf) return; // sender's own echo — finalizeSentMessage already appended it
      const openId = getChatId(selectedChatRef.current);
      if (String(openId || "") !== String(chatId)) return;

      const incomingId =
        messageDocId(message) ||
        `live-${chatId}-${content}-${message.createdAt || message.timestamp || ""}`;
      setMessages((prev) => {
        if (prev.some((item) => sameMessageId(item, incomingId))) return prev;
        // Same text already at the end (poll + socket race) — keep one bubble.
        const last = prev[prev.length - 1];
        if (last && String(last.content || "").trim() === content) {
          if (!messageDocId(last) && messageDocId(message)) {
            const next = prev.slice();
            next[next.length - 1] = {
              ...last,
              ...message,
              _id: messageDocId(message) || last._id,
              content
            };
            return next;
          }
          return prev;
        }
        return [
          ...prev,
          {
            ...message,
            _id: incomingId,
            content,
            createdAt: message.createdAt || message.timestamp || new Date().toISOString()
          }
        ];
      });
      stickToBottomRef.current = true;
    };
    window.addEventListener("adukkumadi:chatSidebarSync", onSidebarSync);
    return () => window.removeEventListener("adukkumadi:chatSidebarSync", onSidebarSync);
  }, [applyIncomingChatToSidebar, getChatId, userId]);

  // Persist active chat so Header can join_chat on Dashboard / any route (not only /communication).
  useEffect(() => {
    const id = getChatId(selectedChat);
    if (!id) {
      localStorage.removeItem(WS_ACTIVE_CHAT_ID_KEY);
      return;
    }
    localStorage.setItem(WS_ACTIVE_CHAT_ID_KEY, id);
    window.dispatchEvent(
      new CustomEvent("adukkumadi:activeChatChanged", { detail: { chatId: id } })
    );
  }, [selectedChat, getChatId]);

  const applyPrivateDelete = useCallback(
    (targetChatId, messageId, deletedBy, sourceMessage) => {
      const adminDelete = String(deletedBy || "").toLowerCase() === "admin";
      const selectedId = getChatId(selectedChatRef.current);
      const chatIsOpen = String(selectedId || "") === String(targetChatId || "");
      if (chatIsOpen) {
        setMessages((prev) => {
          const next = adminDelete
            ? (() => {
                let found = false;
                const mapped = prev.map((item) => {
                  if (!sameMessageId(item, messageId)) return item;
                  found = true;
                  return asDeletedGroupMessage(item);
                });
                return found
                  ? mapped
                  : [...mapped, deletedGroupNotice(messageId, sourceMessage)];
              })()
            : prev.filter((item) => !sameMessageId(item, messageId));
          const last = next[next.length - 1];
          setChats((chats) =>
            chats.map((chat) => {
              if (String(getChatId(chat)) !== String(targetChatId)) return chat;
              if (!last) return { ...chat, lastMessage: null };
              const deleted = isDeletedGroupMessage(last);
              return {
                ...chat,
                lastMessage: {
                  content: deleted ? DELETED_GROUP_MESSAGE_TEXT : last.content || "",
                  timestamp: last.createdAt || new Date().toISOString(),
                  messageType: deleted ? "deleted" : last.messageType || "text"
                }
              };
            })
          );
          return next;
        });
        return;
      }
      if (!adminDelete) return;
      setChats((chats) =>
        chats.map((chat) => {
          if (String(getChatId(chat)) !== String(targetChatId)) return chat;
          return {
            ...chat,
            lastMessage: {
              ...(chat.lastMessage || {}),
              content: DELETED_GROUP_MESSAGE_TEXT,
              messageType: "deleted",
              timestamp: new Date().toISOString()
            }
          };
        })
      );
    },
    [getChatId]
  );

  // Socket event handlers (stable; use refs to access latest selected chat)
  const handleNewMessage = useCallback(
    (data) => {
      const raw = data || {};

      let message =
        raw.message ||
        raw.msg ||
        raw.data?.message ||
        raw.payload?.message ||
        raw.result?.message;

      let chatId =
        raw.chatId ||
        raw.data?.chatId ||
        raw.payload?.chatId ||
        raw.result?.chatId;

      if (!message && raw.content != null && (raw._id || raw.createdAt)) {
        message = raw;
      }
      if (!message) {
        message = raw.msg || raw;
      }

      if (!chatId) {
        chatId = message?.chatId;
      }

      const eventGroup = groupNameOf(raw.group || raw.groupType || message?.group);
      const privateDeletedBy = String(raw.deletedBy || message?.deletedBy || "")
        .trim()
        .toLowerCase();
      const isPrivateDelete =
        !eventGroup &&
        (raw.type === "message_deleted" ||
          raw.action === "delete_message" ||
          raw.action === "message_deleted" ||
          raw.type === "delete_message");
      if (isPrivateDelete) {
        const deletedMessageId = String(
          raw.messageId || raw.deletedMessageId || message?.messageId || message?._id || ""
        ).trim();
        const deletedChatId = raw.chatId || chatId;
        if (deletedMessageId && deletedChatId) {
          const adminDelete =
            privateDeletedBy === "admin" ||
            String(raw.content || message?.content || "").trim() === DELETED_GROUP_MESSAGE_TEXT;
          applyPrivateDelete(
            deletedChatId,
            deletedMessageId,
            adminDelete ? "admin" : "user",
            message && message !== raw ? message : null
          );
        }
        return;
      }

      if (localStorage.getItem("wsDebug") === "1") {
        // eslint-disable-next-line no-console
        console.log("[Communication] handleNewMessage", { raw, message, chatId });
      }

      const groupName = groupNameOf(raw.group || raw.groupType || message?.group);
      if (groupName === GROUP_OWNERS || groupName === GROUP_TENANTS || groupName === GROUP_EXECUTIVES) {
        const selected = selectedChatRef.current;
        const selectedGroup =
          selected?.groupType || (selected?.isBroadcastAllTenants ? GROUP_TENANTS : "");
        const senderId =
          message?.senderId?._id || message?.senderId || raw.userId || raw.userid || "";
        console.log("[chat] recv group", { group: groupName, senderId, message, raw });
        const deletedMessageId =
          messageDocId({ messageId: raw.messageId || raw.deletedMessageId }) ||
          messageDocId(raw.message) ||
          messageDocId(message);
        const deletedBy = String(raw.deletedBy || message?.deletedBy || "")
          .trim()
          .toLowerCase();
        const messageStatus = String(raw.status || message?.status || "")
          .trim()
          .toLowerCase();
        const placeholderText =
          String(raw.content || message?.content || "").trim() === DELETED_GROUP_MESSAGE_TEXT;
        const isGroupDelete =
          raw.action === "delete_group_message" ||
          raw.action === "group_message_deleted" ||
          raw.type === "group_message_deleted" ||
          raw.type === "delete_group_message" ||
          raw.type === "group_message_updated" ||
          raw.action === "group_message_updated" ||
          deletedBy === "admin" ||
          (messageStatus === "inactive" && deletedBy !== "user") ||
          placeholderText;
        console.log("[chat] recv group delete", {
          isGroupDelete,
          deletedMessageId,
          openGroup: selectedGroup,
          group: groupName,
          action: raw.action,
          type: raw.type
        });
        if (isGroupDelete) {
          if (deletedMessageId) {
            deletedGroupMessageIdsRef.current.add(`${groupName}:${deletedMessageId}`);
            const idInOpenThread = messagesRef.current.some((item) =>
              sameMessageId(item, deletedMessageId)
            );
            if (selectedGroup === groupName || idInOpenThread) {
              setMessages((prev) => {
                let found = false;
                const next = prev.map((item) => {
                  if (!sameMessageId(item, deletedMessageId)) return item;
                  found = true;
                  return asDeletedGroupMessage({ ...item, _id: deletedMessageId });
                });
                if (found) return next;
                if (selectedGroup !== groupName) return next;
                return [
                  ...next,
                  deletedGroupNotice(
                    deletedMessageId,
                    message && message !== raw ? message : { content: raw.content, group: groupName }
                  )
                ];
              });
            }
          }
          setGroupSummaries((summaries) => ({
            ...summaries,
            [groupName]: {
              text: DELETED_GROUP_MESSAGE_TEXT,
              at: new Date().toISOString(),
              messageId: deletedMessageId || null
            }
          }));
          return;
        }
        if (isUserDeletedMessage(message) || isUserDeletedMessage(raw)) return;
        const incomingGroupMessage = isDeletedGroupMessage(message)
          ? asDeletedGroupMessage(message)
          : message;
        if (selectedGroup === groupName && incomingGroupMessage) {
          setMessages((prev) => {
            const incomingId = messageDocId(incomingGroupMessage);
            if (incomingId && prev.some((item) => sameMessageId(item, incomingId))) {
              return prev.map((item) =>
                sameMessageId(item, incomingId) ? { ...item, ...incomingGroupMessage, _id: incomingId } : item
              );
            }
            const echoIndex = prev.findIndex((item) => {
              const local = messageDocId(item);
              if (!item?.isBroadcastEcho && !local.startsWith("broadcast-local-")) return false;
              return (
                String(item?.content || "").trim() ===
                String(message?.content || raw.content || incomingGroupMessage?.content || "").trim()
              );
            });
            if (echoIndex >= 0 && messageDocId(incomingGroupMessage)) {
              const next = prev.slice();
              next[echoIndex] = {
                ...incomingGroupMessage,
                _id: messageDocId(incomingGroupMessage),
                isBroadcastEcho: false
              };
              return next;
            }
            if (echoIndex >= 0) return prev;
            return [...prev, incomingGroupMessage];
          });
        }
        const previewSource = isDeletedGroupMessage(incomingGroupMessage)
          ? asDeletedGroupMessage(incomingGroupMessage)
          : incomingGroupMessage;
        const preview = String(previewSource?.content || raw.content || "").trim();
        const senderLabel = isDeletedGroupMessage(previewSource) ? "" : messageSenderName(previewSource);
        if (preview) {
          const labeled = senderLabel ? `${senderLabel}: ${preview}` : preview;
          setGroupSummaries((prev) => ({
            ...prev,
            [groupName]: {
              text: labeled.length > 72 ? `${labeled.slice(0, 72)}…` : labeled,
              at: message?.createdAt || new Date().toISOString(),
              messageId: message?._id || null
            }
          }));
        }
        return;
      }

      if (isUserDeletedMessage(message)) return;
      if (isDeletedGroupMessage(message)) {
        message = asDeletedGroupMessage(message);
      }

      if (!message || !chatId) {
        console.warn("[Communication] Missing message or chatId:", { message, chatId, raw });
        return;
      }
      console.log("[chat] recv private", { chatId, message, raw });

      const selected = selectedChatRef.current;
      const selectedId = getChatId(selected);
      const sameChat = String(selectedId || "") === String(chatId || "");
      const knownChat = chatsRef.current.some(
        (row) => String(getChatId(row) || "") === String(chatId)
      );

      // Ignore private events for chats that are not in this user's sidebar and not open.
      // Stops admin UI from absorbing tenant↔executive DMs that the socket wrongly broadcasts.
      if (!knownChat && !sameChat) {
        console.log("[chat] ignore private for unknown chat", { chatId });
        return;
      }

      if (sameChat) {
        setMessages((prev) => {
          const incomingId = messageDocId(message);
          if (incomingId && prev.some((m) => messageDocId(m) === incomingId)) {
            return prev;
          }
          // Skip WS echo of our own message — finalizeSentMessage already appended it
          const senderRaw = message?.senderId?._id ?? message?.senderId ?? null;
          const normalizedSender =
            senderRaw != null && senderRaw !== "" ? normalizeWsId(String(senderRaw)) : "";
          const normalizedSelf = normalizeWsId(String(userId || ""));
          if (normalizedSender && normalizedSelf && normalizedSender === normalizedSelf) {
            return prev;
          }
          return [...prev, message];
        });
      }

      applyIncomingChatToSidebar(chatId, message);
    },
    [applyIncomingChatToSidebar, applyPrivateDelete, getChatId]
  );

  const handleUserTyping = useCallback(
    (data) => {
      const { chatId, userId: typingUserId, isTyping } = data || {};
      if (!chatId || !typingUserId) return;

      const selected = selectedChatRef.current;
      if (!selected) return;

      if (getChatId(selected) === chatId && typingUserId !== userId) {
        setTypingUsers((prev) => {
          if (isTyping) return [...prev.filter((id) => id !== typingUserId), typingUserId];
          return prev.filter((id) => id !== typingUserId);
        });
      }
    },
    [getChatId, userId]
  );

  const handleMessagesRead = useCallback(
    (data) => {
      const { chatId, userId: readerId } = data || {};
      if (!chatId || !readerId) return;

      const selected = selectedChatRef.current;
      if (selected && getChatId(selected) === chatId) {
        setMessages((prev) =>
          prev.map((msg) => ({
            ...msg,
            readBy: [...(msg.readBy || []), { userId: readerId, readAt: new Date().toISOString() }]
          }))
        );
      }
    },
    [getChatId]
  );

  const handleUserOnline = useCallback((data) => {
    const id = data?.userId;
    if (!id) return;
    setOnlineUsers((prev) => new Set([...prev, id]));
  }, []);

  const handleUserOffline = useCallback((data) => {
    const id = data?.userId;
    if (!id) return;
    setOnlineUsers((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Mark messages as read
  const markMessagesAsRead = useCallback(
    async (chatId) => {
      if (!userId || !chatId) return;
      setChats((prev) =>
        prev.map((c) => {
          const id = getChatId(c);
          if (id !== chatId) return c;
          return { ...c, unreadForMe: 0, unreadCount: 0 };
        })
      );
      sendWs({ action: "messages_read", chatId, ...wsIdentity });
    },
    [userId, getChatId, wsIdentity]
  );

  const recoverDirectChatRef = useRef(null);

  // Load history when opening a chat. New messages rely on WebSocket only (no polling).
  const fetchMessages = useCallback(
    async (chatId, page = 1, { allowRecover = true } = {}) => {
      if (!API_BASE_URL || !userId || !chatId) return;

      setMessagesLoading(true);

      try {
        const payload = {
          action: "chat.getMessages",
          userid: userId,
          chatId,
          page,
          limit: messageLimit
        };

        const response = await axios.post(API_BASE_URL, payload);
        const data = response?.data;

        if (isApiSuccess(data)) {
          const msgs = data.messages || data.data?.messages || [];
          setMessages(visibleThreadMessages(msgs));
          return;
        }

        const errText = data?.error || data?.message || "Failed to load messages";
        if (allowRecover && isNotParticipantError(errText) && recoverDirectChatRef.current) {
          setMessagesLoading(false);
          const recovered = await recoverDirectChatRef.current(errText);
          if (recovered) return;
        }
        toast.error(errText);
      } catch (error) {
        const msg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong";
        if (allowRecover && isNotParticipantError(msg) && recoverDirectChatRef.current) {
          setMessagesLoading(false);
          const recovered = await recoverDirectChatRef.current(msg);
          if (recovered) return;
        }
        toast.error(msg);
      } finally {
        setMessagesLoading(false);
      }
    },
    [API_BASE_URL, userId, messageLimit]
  );

  const applyGroupPreview = useCallback((groupType, message) => {
    const deleted = isDeletedGroupMessage(message);
    const preview = deleted
      ? DELETED_GROUP_MESSAGE_TEXT
      : String(message?.content || "").trim();
    if (!groupType || !preview) return;
    const senderLabel = deleted ? "" : messageSenderName(message);
    const labeled = senderLabel ? `${senderLabel}: ${preview}` : preview;
    setGroupSummaries((prev) => ({
      ...prev,
      [groupType]: {
        text: labeled.length > 72 ? `${labeled.slice(0, 72)}…` : labeled,
        at: message?.createdAt || new Date().toISOString(),
        messageId: message?._id || null
      }
    }));
  }, []);

  const fetchGroupMessages = useCallback(
    async (groupType, { silent = false, updateThread = false } = {}) => {
      if (!API_BASE_URL || !userId || !groupType) return;
      if (!silent) setMessagesLoading(true);
      const payload = {
        action: "chat.getGroupMessages",
        userid: userId,
        group: groupType,
        page: 1,
        limit: messageLimit
      };
      console.log("[chat] getGroupMessages", payload);
      try {
        const response = await axios.post(API_BASE_URL, payload);
        const data = response?.data;
        console.log("[chat] getGroupMessages response", {
          status: data?.status,
          message: data?.message,
          count: Array.isArray(data?.messages) ? data.messages.length : 0,
          pagination: data?.pagination
        });
        if (!isApiSuccess(data)) {
          if (!silent) toast.error(data?.error || data?.message || "Failed to load group messages");
          return;
        }
        const msgs = visibleThreadMessages(Array.isArray(data?.messages) ? data.messages : []);
        const list = msgs
          .map((item) =>
            isDeletedGroupMessage(item) ||
            deletedGroupMessageIdsRef.current.has(`${groupType}:${messageDocId(item)}`)
              ? asDeletedGroupMessage(item)
              : item
          )
          .slice()
          .sort((a, b) => {
          const left = Date.parse(a?.createdAt || "") || 0;
          const right = Date.parse(b?.createdAt || "") || 0;
          return left - right;
        });
        const seenIds = new Set(list.map((item) => messageDocId(item)));
        deletedGroupMessageIdsRef.current.forEach((key) => {
          const prefix = `${groupType}:`;
          if (!key.startsWith(prefix)) return;
          const messageId = key.slice(prefix.length);
          if (!messageId || seenIds.has(messageId)) return;
          list.push(deletedGroupNotice(messageId, { group: groupType }));
        });
        if (!silent || updateThread) {
          setMessages((prev) => {
            const locallyDeleted = new Set(
              prev.filter((item) => isDeletedGroupMessage(item)).map((item) => messageDocId(item))
            );
            return list.map((item) =>
              locallyDeleted.has(messageDocId(item)) ? asDeletedGroupMessage(item) : item
            );
          });
        }
        if (list.length) applyGroupPreview(groupType, list[list.length - 1]);
      } catch (error) {
        if (!silent) {
          toast.error(
            error?.response?.data?.message || error?.message || "Failed to load group messages"
          );
        }
      } finally {
        if (!silent) setMessagesLoading(false);
      }
    },
    [API_BASE_URL, userId, messageLimit, applyGroupPreview]
  );

  // Chat listeners only: Header opens the shared WebSocket for the whole app.
  // We ensure the socket is ready (reuse Header’s connection or connect if user opened /communication first).
  useEffect(() => {
    if (!userId || !WS_URL) {
      console.warn("Communication: Missing userId or WS_URL", { userId, WS_URL });
      return;
    }

    let cancelled = false;

    console.log(
      "Communication: attaching chat listeners (shared WebSocket from Header)...",
      { WS_URL, userId, userRole }
    );

    (async () => {
      const ok = await ensureWsConnected();
      if (cancelled) return;
      if (ok) {
        console.log("Communication: shared WebSocket ready");
        setWsConnected(true);
        const selected = selectedChatRef.current;
        const chatId = getChatId(selected);
        if (chatId) {
          // eslint-disable-next-line no-console
          console.log("[Communication] WS ready; join_chat:", chatId);
          sendWs({ action: "join_chat", chatId, ...wsIdentity });
        }
      } else {
        setWsConnected(false);
      }
    })();

    const unsubscribe = addWsListener((payload) => {
      if (localStorage.getItem("wsDebug") === "1") {
        // eslint-disable-next-line no-console
        console.log("[Communication] WebSocket message received:", payload);
      }

      // Update connection status
      setWsConnected(isWsOpen());

      // Handle message history responses (not used - we use HTTP API)
      // if (
      //   payload?.action === "chat.getMessages" ||
      //   payload?.type === "messages" ||
      //   payload?.type === "chat_messages"
      // ) {
      //   // This is handled by HTTP API now
      //   return;
      // }

      // Handle chat create/get response
      if (payload?.action === "admin.getOrCreateChat" || payload?.type === "chat") {
        const chat = payload?.chat || payload?.data?.chat || payload?.data;
        if (chat?._id) {
          pendingCreateChatTargetRef.current = null;

          const normalized = {
            ...chat,
            chatId: chat.chatId || chat._id || null,
            unreadForMe: typeof chat.unreadForMe === "number" ? chat.unreadForMe : undefined
          };

          setChats((prev) => mergeNormalizedChatIntoList(prev, normalized));

          setSelectedChat(normalized);
          // Join the room and request message history (socket-only mode)
          if (normalized.chatId) {
            sendWs({ action: "join_chat", chatId: normalized.chatId, ...wsIdentity });
            fetchMessages(normalized.chatId);
          }

          // If we queued a message while creating this chat, send it now
          const queued = pendingSendAfterChatCreateRef.current;
          if (queued?.messageText) {
            pendingSendAfterChatCreateRef.current = null;
            const wsSent = sendWs({
              action: "send_message",
              chatId: normalized.chatId,
              ...wsIdentity,
              content: queued.messageText,
              messageType: "text"
            });
            if (!wsSent) toast.warning("WebSocket not connected. Message not sent.");
          }
        }
        setLoading(false);
        return;
      }
      
      const inboundGroup = groupNameOf(
        payload?.group || payload?.groupType || payload?.message?.group
      );
      const groupEventAction = String(payload?.action || payload?.type || "");
      const isGroupChatEvent =
        (inboundGroup === GROUP_OWNERS ||
          inboundGroup === GROUP_TENANTS ||
          inboundGroup === GROUP_EXECUTIVES) &&
        groupEventAction !== "user_typing" &&
        groupEventAction !== "user_online" &&
        groupEventAction !== "user_offline" &&
        groupEventAction !== "messages_read";
      // Normalize common server shapes into { chatId, message }
      const isInboundChatEvent =
        isGroupChatEvent ||
        payload?.type === "new_message" ||
        payload?.type === "NEW_MESSAGE" ||
        payload?.action === "new_message" ||
        payload?.event === "new_message" ||
        payload?.type === "message" ||
        payload?.type === "chat_message" ||
        payload?.type === "broadcast" ||
        payload?.action === "send_message" ||
        payload?.action === "send_group_message" ||
        payload?.action === "chat.sendMessage" ||
        payload?.action === "chat.newMessage" ||
        payload?.action === "message_created" ||
        payload?.type === "group_message" ||
        payload?.type === "group_message_updated" ||
        payload?.action === "group_message_updated" ||
        payload?.type === "group_message_deleted" ||
        payload?.action === "group_message_deleted" ||
        payload?.type === "delete_group_message" ||
        payload?.action === "delete_group_message" ||
        payload?.type === "message_deleted" ||
        payload?.action === "delete_message" ||
        payload?.action === "message_deleted" ||
        payload?.type === "delete_message";

      if (isInboundChatEvent) {
        if (localStorage.getItem("wsDebug") === "1") {
          // eslint-disable-next-line no-console
          console.log("[Communication] Handling new message:", payload);
        }
        
        // Enhanced debugging for group message deletion
        if (payload?.action === "delete_group_message" || payload?.type === "group_message_updated") {
          console.log("[Communication] 🗑️ GROUP MESSAGE DELETE RECEIVED!", payload);
          toast.info(`🗑️ Group message deletion WebSocket received: ${payload.messageId}`);
        }
        
        handleNewMessage(payload);
        return;
      }

      if (payload?.type === "user_typing" || payload?.action === "user_typing") {
        console.log("[Communication] Handling typing:", payload);
        handleUserTyping(payload);
        return;
      }

      if (payload?.type === "messages_read" || payload?.action === "messages_read") {
        console.log("[Communication] Handling messages read:", payload);
        handleMessagesRead(payload);
        return;
      }

      if (payload?.type === "user_online" || payload?.action === "user_online") {
        console.log("[Communication] Handling user online:", payload);
        handleUserOnline(payload);
        return;
      }

      if (payload?.type === "user_offline" || payload?.action === "user_offline") {
        console.log("[Communication] Handling user offline:", payload);
        handleUserOffline(payload);
        return;
      }

      // Some servers send direct { chatId, message } without type
      if (payload?.chatId && (payload?.message || payload?.content)) {
        console.log("[Communication] Handling direct message:", payload);
        handleNewMessage(payload);
      } else {
        console.log("[Communication] Unhandled WebSocket message:", payload);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [
    WS_URL,
    userId,
    userRole,
    ensureWsConnected,
    handleNewMessage,
    handleUserTyping,
    handleMessagesRead,
    handleUserOnline,
    handleUserOffline,
    getChatId,
    wsIdentity,
    fetchMessages
  ]);

  // Periodic WebSocket status check
  useEffect(() => {
    const interval = setInterval(() => {
      setWsConnected(isWsOpen());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Create or get a one-to-one chat. Admins use the admin action; everyone else uses the user action.
  // Active executives (OWNER/TENANT + executiveStatus active) must use user.getOrCreateChat so
  // owner/tenant DMs are not mixed with the admin↔executive chat.
  const createOrGetChat = useCallback(
    async (targetUserId, hintParticipant, { loadMessages = true } = {}) => {
      if (!userId) return null;
      if (!targetUserId) return null;
      if (!API_BASE_URL) {
        toast.error("API URL not configured");
        return null;
      }

      try {
        const payload = {
          action: isAdminCapable ? "admin.getOrCreateChat" : "user.getOrCreateChat",
          userid: userId,
          targetUserId: String(targetUserId)
        };

        // eslint-disable-next-line no-console
        console.log("[Communication] getOrCreateChat", payload);

        const response = await axios.post(API_BASE_URL, payload);
        const data = response?.data;

        const createdChat = data?.chat;
        const createdChatId = createdChat?._id || createdChat?.chatId;
        if (data?.status !== "success" || !createdChatId) {
          toast.error(data?.message || "Failed to create chat");
          return null;
        }

        const chat = createdChat;
        const chatId = String(createdChatId);
        if (!isDirectChatBetween(chat, userId, targetUserId)) {
          console.warn("[Communication] getOrCreateChat returned wrong participants", {
            chatId,
            targetUserId,
            participants: participantUserIds(chat)
          });
          toast.error(
            "Opened the wrong chat for this person. Please try again or contact support."
          );
          return null;
        }
        const participantRecord = chat.participants?.find((p) => {
          const pid = p?.userId?._id || p?.userId?.id || p?.userId || p?._id;
          return String(pid || "") !== String(userId);
        });
        const otherFromChat = participantRecord?.userId?._id
          ? participantRecord.userId
          : participantRecord?.userId
          ? { _id: participantRecord.userId, name: participantRecord.name, email: participantRecord.email }
          : null;
        // Prefer the requested peer id so we never label an executive DM as Admin.
        const otherParticipant = {
          ...(hintParticipant || {}),
          ...(otherFromChat || {}),
          _id: String(targetUserId),
          id: String(targetUserId)
        };

        const normalized = {
          ...chat,
          _id: chatId,
          chatId,
          otherParticipant,
          directoryRole:
            hintParticipant?.directoryRole ||
            normalizeDirectoryRole(otherParticipant?.role),
          userId: String(targetUserId),
          participantId: String(targetUserId),
          unreadForMe: 0
        };

        // Merge into list so admin can click it again (replace orphan tenant row — no duplicates)
        setChats((prev) => mergeNormalizedChatIntoList(prev, normalized));

        setSelectedChat(normalized);

        // Join room + load messages over WS
        sendWs({ action: "join_chat", chatId, ...wsIdentity });
        if (loadMessages) {
          // Mark as read immediately — user clicked into this conversation
          markMessagesAsRead(chatId);
          fetchMessages(chatId, 1, { allowRecover: false });
        }

        return normalized;
      } catch (error) {
        toast.error(
          error?.response?.data?.message || error?.message || "Failed to create chat"
        );
        return null;
      }
    },
    [userId, isAdminCapable, API_BASE_URL, fetchMessages, wsIdentity, markMessagesAsRead]
  );

  const recoverDirectChatIfNeeded = useCallback(
    async (errorMessage, preferredPeerId) => {
      if (!isNotParticipantError(errorMessage)) return null;
      const selected = selectedChatRef.current;
      const peerId = String(preferredPeerId || peerUserIdOfChat(selected) || "").trim();
      if (!peerId) {
        toast.error(errorMessage);
        return null;
      }
      console.log("[Communication] recovering direct chat", {
        peerId,
        badChatId: getChatId(selected),
        errorMessage
      });
      return createOrGetChat(
        peerId,
        {
          ...(selected?.otherParticipant || {}),
          _id: peerId,
          directoryRole: selected?.directoryRole
        },
        { loadMessages: true }
      );
    },
    [createOrGetChat, getChatId]
  );
  recoverDirectChatRef.current = recoverDirectChatIfNeeded;

  const openMobileThreadIfNarrow = useCallback(() => {
    if (typeof window !== "undefined" && window.matchMedia(MOBILE_CHAT_MQ).matches) {
      setMobileThreadOpen(true);
    }
  }, []);

  // Handle chat selection
  const handleChatSelect = useCallback(
    async (chat) => {
      if (!chat) return;

      // Ensure socket is open before join/leave (fixes tenant "Join result: false")
      const wsOk = await ensureWsConnected();
      if (!wsOk) {
        toast.error("WebSocket not connected");
        return;
      }

      // Stay joined to previous private rooms. Leaving drops live DMs while viewing
      // another thread (e.g. Owners group) or Dashboard.
      setTypingUsers([]);
      stickToBottomRef.current = true;
      setMessages([]);
      setNewMessage("");
      if (messageTextareaRef.current) {
        messageTextareaRef.current.style.height = "auto";
        messageTextareaRef.current.style.overflowY = "hidden";
      }
      setPendingAttachments((prev) => {
        prev.forEach((p) => {
          if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        });
        return [];
      });

      if (chat.isGroup || chat.groupType || chat.isBroadcastAllTenants) {
        const groupType = chat.groupType || GROUP_TENANTS;
        setSelectedChat(chat);
        setMessages([]);
        openMobileThreadIfNarrow();
        fetchGroupMessages(groupType);
        return;
      }

      let chatId = getChatId(chat);
      const targetUserId = peerUserIdOfChat(chat);
      const peerRole = normalizeDirectoryRole(chat.directoryRole);
      // Non-admin private DMs (tenant/owner/executive) always resolve via getOrCreate for the
      // selected peer so tenant→executive never reuses the admin↔executive chat id.
      const mustResolveDirectDm =
        !isAdminCapable &&
        Boolean(targetUserId) &&
        (!chatId ||
          peerRole === "EXECUTIVE" ||
          peerRole === "OWNER" ||
          peerRole === "TENANT" ||
          peerRole === "ADMIN" ||
          isActiveExecutive);

      if (mustResolveDirectDm) {
        setSelectedChat(chat);
        setLoading(true);
        const created = await createOrGetChat(targetUserId, {
          ...(chat.otherParticipant || {}),
          _id: targetUserId,
          directoryRole: chat.directoryRole || peerRole
        });
        setLoading(false);
        if (created?.chatId || created?._id) {
          openMobileThreadIfNarrow();
          return;
        }
        if (!chatId) {
          toast.error("Failed to create chat");
          return;
        }
        // Fall through and try the existing chatId if getOrCreate failed.
      }

      // Normal select
      setSelectedChat(chat);

      if (chatId) {
        console.log("[Communication] Joining chat room:", chatId, "for user:", userId);
        const joinMessage = { 
          action: "join_chat", 
          chatId, 
          ...wsIdentity
        };
        console.log("[Communication] Join message:", joinMessage);
        const joinResult = sendWs(joinMessage);
        console.log("[Communication] Join result:", joinResult);
        
        if (!joinResult) {
          console.warn("[Communication] Failed to join chat room - WebSocket not connected");
          toast.warning("Selected chat but real-time messaging may not work");
        }
        // Mark as read as soon as the user clicks into this conversation
        markMessagesAsRead(chatId);
        fetchMessages(chatId);
      } else {
        console.warn("[Communication] No chatId found for selected chat:", chat);
      }

      openMobileThreadIfNarrow();
    },
    [
      ensureWsConnected,
      userId,
      createOrGetChat,
      fetchMessages,
      fetchGroupMessages,
      getChatId,
      wsIdentity,
      openMobileThreadIfNarrow,
      isActiveExecutive,
      isAdminCapable,
      markMessagesAsRead
    ]
  );

  const handleChatSelectRef = useRef(handleChatSelect);
  handleChatSelectRef.current = handleChatSelect;

  const joinAllPrivateChatRooms = useCallback(
    (chatList) => {
      if (!userId || !isWsOpen()) return;
      const ids = [];
      (Array.isArray(chatList) ? chatList : []).forEach((chat) => {
        if (chat?.isGroup || chat?.groupType || chat?.isBroadcastAllTenants) return;
        const chatId = getChatId(chat);
        if (!chatId) return;
        const id = String(chatId);
        if (ids.includes(id)) return;
        ids.push(id);
        sendWs({ action: "join_chat", chatId: id, ...wsIdentity });
      });
      if (ids.length) {
        console.log("[Communication] joined all private chats", { count: ids.length, ids });
      }
    },
    [userId, getChatId, wsIdentity]
  );

  // Fetch chats based on user role
  const fetchChats = useCallback(
    async (page = 1, append = false) => {
      if (!userId) {
        // eslint-disable-next-line no-console
        console.error("Communication: No userId found");
        return;
      }
      if (!API_BASE_URL) {
        toast.error("API URL not configured");
        setLoading(false);
        return;
      }

      setLoading(!append);
      try {
        const isAdmin = userRole === "ADMIN" || userRole === "SUPERADMIN";
        const isOwner = userRole === "OWNER";
        const isTenant = userRole === "TENANT";
        const selfEmail = String(localStorage.getItem("email") || "")
          .trim()
          .toLowerCase();

        const safePost = async (payload) => {
          try {
            const response = await axios.post(API_BASE_URL, payload);
            const body = response?.data;
            const status = String(body?.status || "").toLowerCase();
            if (status === "success" || body?.success === true || pickApiList(body).length > 0) {
              return body;
            }
            return null;
          } catch {
            return null;
          }
        };

        const payload = {
          action: "user.getChats",
          userid: userId,
          page,
          limit: chatLimit,
          ...(searchQuery ? { search: searchQuery } : {})
        };

        let data = null;
        try {
          const response = await axios.post(API_BASE_URL, payload);
          data = response?.data;
        } catch (error) {
          if (!isAdmin) throw error;
          console.warn(
            "[Communication] user.getChats failed",
            error?.response?.data?.message || error?.message
          );
        }
        const chatsOk = data?.status === "success";

        if (chatsOk || (isAdmin && !append)) {
          if (!chatsOk) {
            console.warn("[Communication] user.getChats did not return chats", data?.message || data?.status);
          }
          const newChats = (chatsOk ? data.chats || [] : [])
            .map((c) =>
              withChatPeer(
                {
                  ...c,
                  chatId: c.chatId || c._id || null,
                  unreadForMe: typeof c.unreadForMe === "number" ? c.unreadForMe : undefined
                },
                userId
              )
            )
            .filter((chat) => chatIncludesSelf(chat, userId))
            .filter((chat) => {
              if (!isAdmin) return true;
              return Boolean(chat?.chatId || chat?._id);
            });

          let merged = newChats;
          if (!append) {
            const directory = [];
            const community = await safePost({
              action: "community.getAll",
              page: 1,
              limit: 100,
              view: "present",
              userId,
              userid: userId
            });
            const executiveRows = pickApiList(community)
              .filter(isActiveExecutiveMember)
              .map((member) => contactFromUser(member, "EXECUTIVE"))
              .filter(Boolean);
            const selfIsExec = executiveRows.some((row) => {
              const email = String(row.otherParticipant?.email || "")
                .trim()
                .toLowerCase();
              return row.userId === userId || (email && email === selfEmail);
            });
            setIsActiveExecutive(selfIsExec);
            setExecutiveSenderIds(new Set(executiveRows.map((row) => String(row.userId))));

            if (!isAdmin) {
              const adminId = String(localStorage.getItem("adminId") || "").trim();
              if (adminId && adminId !== userId) {
                const adminProfile = await safePost({
                  action: "getProfile",
                  userId: adminId,
                  userid: adminId
                });
                const adminRecord =
                  adminProfile?.profile ||
                  adminProfile?.user ||
                  adminProfile?.data ||
                  adminProfile;
                const adminEmail = String(adminRecord?.email || "").trim();
                const adminRecordId = personIdOf(adminRecord);
                const profileIsAdmin =
                  adminRecord &&
                  (adminRecordId === adminId ||
                    (adminEmail && adminEmail.toLowerCase() !== selfEmail));
                const adminContact = contactFromUser(
                  {
                    userId: adminId,
                    name: profileIsAdmin
                      ? adminRecord?.name || adminRecord?.fullName || "Admin"
                      : "Admin",
                    email: profileIsAdmin ? adminEmail : ""
                  },
                  "ADMIN"
                );
                if (adminContact) directory.push(adminContact);
              }

              executiveRows.forEach((row) => {
                const email = String(row.otherParticipant?.email || "")
                  .trim()
                  .toLowerCase();
                if (row.userId === userId || (email && email === selfEmail)) return;
                directory.push(row);
              });
            }

            if (isOwner || (selfIsExec && !isAdmin)) {
              const tenantPayload = isOwner
                ? {
                    action: "owner.getTenants",
                    userid: userId,
                    page: 1,
                    limit: 100,
                    isActive: true,
                    isVerified: true,
                    sortBy: "createdAt",
                    sortOrder: "desc"
                  }
                : {
                    action: "admin.getTenants",
                    userId,
                    userid: userId,
                    page: 1,
                    limit: 100,
                    role: "TENANT"
                  };
              let tenantData = await safePost(tenantPayload);
              if (isOwner && pickApiList(tenantData).length === 0) {
                tenantData = await safePost({
                  action: "owner.getTenants",
                  userid: userId,
                  page: 1,
                  limit: 100,
                  sortBy: "createdAt",
                  sortOrder: "desc"
                });
              }
              pickApiList(tenantData).forEach((tenant) => {
                const row = contactFromUser(tenant, "TENANT");
                if (row && row.userId !== userId) directory.push(row);
              });
            }

            if (selfIsExec && !isAdmin) {
              const ownerData = await safePost({
                action: "admin.getTenants",
                userId,
                userid: userId,
                page: 1,
                limit: 100,
                role: "OWNER"
              });
              pickApiList(ownerData).forEach((owner) => {
                const row = contactFromUser(owner, "OWNER");
                if (row && row.userId !== userId) directory.push(row);
              });
            }

            if (isAdmin) {
              const ownerData = await safePost({
                action: "admin.getTenants",
                userId,
                userid: userId,
                page: 1,
                limit: 100,
                role: "OWNER"
              });
              const tenantData = await safePost({
                action: "admin.getTenants",
                userId,
                userid: userId,
                page: 1,
                limit: 100,
                role: "TENANT"
              });
              pickApiList(ownerData).forEach((owner) => {
                const row = contactFromUser(owner, "OWNER");
                if (row && row.userId !== userId) directory.push(row);
              });
              pickApiList(tenantData).forEach((tenant) => {
                const row = contactFromUser(tenant, "TENANT");
                if (row && row.userId !== userId) directory.push(row);
              });
              executiveRows.forEach((row) => {
                if (row.userId !== userId) directory.push(row);
              });
              setDirectoryPeople(dedupeSidebarChats(directory));
            }

            const ownProfile = await safePost({
              action: "getProfile",
              userId,
              userid: userId
            });
            const own =
              ownProfile?.profile || ownProfile?.user || ownProfile?.data || ownProfile;
            if (String(own?.executiveStatus || "").trim().toLowerCase() === "active") {
              setIsActiveExecutive(true);
            }
            if (isTenant) {
              const ownerId = personIdOf(own?.owner || own?.property?.owner || {});
              const fallbackOwnerId = String(
                own?.ownerId || own?.property?.ownerId || ownerId || ""
              ).trim();
              if (fallbackOwnerId && fallbackOwnerId !== userId) {
                const ownerContact = contactFromUser(
                  {
                    userId: fallbackOwnerId,
                    name:
                      own?.ownerName ||
                      own?.owner?.name ||
                      own?.property?.ownerName ||
                      "Owner",
                    email: own?.owner?.email || own?.property?.ownerEmail || ""
                  },
                  "OWNER"
                );
                if (ownerContact) directory.push(ownerContact);
              }
            }

            console.log("[Communication] people for this role", {
              role: userRole,
              admins: directory.filter((row) => row.directoryRole === "ADMIN").length,
              executives: directory.filter((row) => row.directoryRole === "EXECUTIVE").length,
              tenants: directory.filter((row) => row.directoryRole === "TENANT").length,
              owners: directory.filter((row) => row.directoryRole === "OWNER").length
            });

            // Owners normally only see admin/exec/tenants. Active executives may also DM owners.
            const allowedOwnerRoles = new Set(
              selfIsExec || String(own?.executiveStatus || "").trim().toLowerCase() === "active"
                ? ["ADMIN", "EXECUTIVE", "TENANT", "OWNER"]
                : ["ADMIN", "EXECUTIVE", "TENANT"]
            );
            merged = newChats.map((chat) => ({ ...chat }));
            directory.forEach((contact) => {
              const peer = sidebarParticipantKey(contact);
              if (!peer || peer === userId) return;
              const idx = merged.findIndex((chat) => sidebarParticipantKey(chat) === peer);
              if (idx === -1) {
                if (!isAdmin) merged.push(contact);
                return;
              }
              const existingParticipant = merged[idx].otherParticipant;
              const contactParticipant = contact.otherParticipant;
              // Never overwrite a real chatId with a directory stub, and never attach another
              // person's chatId onto this peer row (keeps executive↔owner off the admin row).
              merged[idx] = {
                ...merged[idx],
                directoryRole: contact.directoryRole || merged[idx].directoryRole,
                otherParticipant: {
                  ...(existingParticipant || {}),
                  ...(contactParticipant || {}),
                  _id:
                    existingParticipant?._id ||
                    existingParticipant?.id ||
                    contactParticipant?._id ||
                    contactParticipant?.id,
                  id:
                    existingParticipant?.id ||
                    existingParticipant?._id ||
                    contactParticipant?.id ||
                    contactParticipant?._id,
                  name:
                    existingParticipant?.name ||
                    contactParticipant?.name,
                  email: displayEmail(existingParticipant?.email) || displayEmail(contactParticipant?.email)
                }
              };
            });
            if (isOwner) {
              merged = merged.filter(
                (chat) => !chat.directoryRole || allowedOwnerRoles.has(chat.directoryRole)
              );
            }
          }

          if (append) {
            setChats((prev) => {
              const next = dedupeSidebarChats([...prev, ...newChats]);
              joinAllPrivateChatRooms(next);
              return next;
            });
          } else {
            const ready = dedupeSidebarChats(merged);
            setChats(ready);
            joinAllPrivateChatRooms(ready);
            const isNarrowMobile =
              typeof window !== "undefined" && window.matchMedia(MOBILE_CHAT_MQ).matches;
            const { chatId: deepChatId, participantId: deepParticipantId } =
              focusFromRouteRef.current;
            const firstExisting = ready.find((chat) => chat?.chatId || chat?._id);
            if (
              !selectedChatRef.current &&
              firstExisting &&
              !isNarrowMobile &&
              !deepChatId &&
              !deepParticipantId
            ) {
              handleChatSelectRef.current?.(firstExisting);
            }
          }

          const pagination = chatsOk ? data.pagination || {} : {};
          setHasMoreChats(chatsOk && page < (pagination.pages || 1));
        } else {
          toast.error(data?.message || "Failed to load chats");
        }
      } catch (error) {
        const msg =
          error?.response?.data?.message || error?.message || "Something went wrong";
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    },
    [userId, API_BASE_URL, userRole, chatLimit, searchQuery, joinAllPrivateChatRooms]
  );

  const finalizeSentMessage = useCallback(
    (chatId, newMsg) => {
      if (!newMsg) return;
      stickToBottomRef.current = true;
      const messageType = newMsg.messageType || "text";
      const lastContent =
        newMsg.content != null && String(newMsg.content).trim() !== ""
          ? newMsg.content
          : newMsg.fileName || "";
      setMessages((prev) => [...prev, newMsg]);
      setChats((prev) =>
        prev.map((c) => {
          const id = getChatId(c);
          if (id !== chatId) return c;
          return {
            ...c,
            lastMessage: {
              content: lastContent,
              senderId: userId,
              timestamp: newMsg.createdAt,
              messageType,
              ...(newMsg.fileName ? { fileName: newMsg.fileName } : {})
            }
          };
        })
      );
      const selected = selectedChatRef.current;
      const recipientUserId =
        selected?.otherParticipant?._id ||
        selected?.otherParticipant?.id ||
        selected?.otherParticipant?.userId ||
        selected?.userId ||
        null;
      const wsMessage = {
        ...wsIdentity,
        userid: userId,
        id: userId,
        action: "send_message",
        type: "new_message",
        chatId,
        userId,
        content: lastContent,
        messageType,
        message: newMsg,
        ...(recipientUserId
          ? {
              recipientId: String(recipientUserId),
              recipientUserId: String(recipientUserId),
              targetUserId: String(recipientUserId)
            }
          : {})
      };
      console.log("[chat] send text", wsMessage, { socket: getWsDebugSnapshot() });
      const wsSent = isWsOpen() && sendWs(wsMessage);
      if (!wsSent) {
        connectWs({ baseUrl: process.env.REACT_APP_WEBSOCKET_URL, userId, userType: userRole })
          .then(() => {
            const retried = sendWs(wsMessage);
            console.log("[chat] send text retry", retried, getWsDebugSnapshot());
          })
          .catch((error) => {
            console.warn("[chat] text socket send failed", error?.message || error);
          });
      }
    },
    [getChatId, userId, userRole, wsIdentity]
  );

  const postChatMessage = useCallback(
    async (chatId, body, { allowRecover = true } = {}) => {
      const { messageType = "text", content, fileData } = body;
      const payload = {
        action: "chat.sendMessage",
        userid: userId,
        chatId,
        messageType
      };
      if (content != null && String(content).trim() !== "") {
        payload.content = content;
      }
      if (fileData && typeof fileData === "object") {
        payload.fileData = fileData;
      }
      try {
        const response = await axios.post(API_BASE_URL, payload);
        const data = response?.data;
        if (!isApiSuccess(data)) {
          const errText = data?.error || data?.message || "Failed to send message";
          if (allowRecover && isNotParticipantError(errText) && recoverDirectChatRef.current) {
            const recovered = await recoverDirectChatRef.current(errText);
            const nextId = getChatId(recovered);
            if (nextId) {
              return postChatMessage(nextId, body, { allowRecover: false });
            }
          }
          return { ok: false, message: errText };
        }
        const newMsg = data.data;
        if (!newMsg || typeof newMsg !== "object") {
          return { ok: false, message: "Invalid response from server" };
        }
        finalizeSentMessage(chatId, newMsg);
        return { ok: true };
      } catch (error) {
        const errText =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to send message";
        if (allowRecover && isNotParticipantError(errText) && recoverDirectChatRef.current) {
          const recovered = await recoverDirectChatRef.current(errText);
          const nextId = getChatId(recovered);
          if (nextId) {
            return postChatMessage(nextId, body, { allowRecover: false });
          }
        }
        return { ok: false, message: errText };
      }
    },
    [API_BASE_URL, userId, finalizeSentMessage, getChatId]
  );

  const addAttachmentsFromFileList = useCallback((fileList) => {
    const incoming = Array.from(fileList || []).filter(Boolean);
    if (!incoming.length) return;
    setPendingAttachments((prev) => {
      const next = [...prev];
      for (let i = 0; i < incoming.length; i++) {
        const file = incoming[i];
        if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
          toast.error(`"${file.name}" is too large (max 15 MB).`);
          continue;
        }
        if (next.length >= MAX_PENDING_ATTACHMENTS) {
          toast.warning(`You can add up to ${MAX_PENDING_ATTACHMENTS} files at once.`);
          break;
        }
        next.push({
          id: `${Date.now()}-${i}-${file.name}`,
          file,
          previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null
        });
      }
      return next;
    });
  }, []);

  const removePendingAttachment = useCallback((id) => {
    setPendingAttachments((prev) => {
      const x = prev.find((p) => p.id === id);
      if (x?.previewUrl) URL.revokeObjectURL(x.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  // Send message
  const sendMessage = useCallback(async () => {
    // Prevent double-send on rapid Enter/button click while HTTP is in flight
    if (isSendingRef.current) return;

    const messageText = newMessage.trim();
    const hasText = Boolean(messageText);
    const attachmentSnapshot = pendingAttachments;

    if (!hasText && attachmentSnapshot.length === 0) return;
    if (!selectedChat || !API_BASE_URL || !userId) return;

    isSendingRef.current = true;

    const selectedGroupType =
      selectedChat.groupType || (selectedChat.isBroadcastAllTenants ? GROUP_TENANTS : "");
    if (selectedChat.isGroup || selectedGroupType) {
      const flags = {
        isAdmin: isAdminCapable,
        isOwner: userRole === "OWNER",
        isTenant: userRole === "TENANT",
        isActiveExecutive
      };
      if (!canPostToGroup(selectedGroupType, flags)) {
        toast.error("You can't send a message to this group.");
        return;
      }
      if (attachmentSnapshot.length > 0) {
        toast.warning("Group messages support text only. Remove attachments.");
        return;
      }
      if (!hasText) return;

      const groupAction =
        selectedGroupType === GROUP_TENANTS
          ? "admin.broadcastMessageToTenants"
          : selectedGroupType === GROUP_OWNERS
          ? "admin.broadcastMessageToOwners"
          : "admin.broadcastMessageToExecutives";

      setNewMessage("");
      try {
        const response = await axios.post(API_BASE_URL, {
          action: groupAction,
          userid: userId,
          content: messageText,
          messageType: "text",
          group: selectedGroupType
        });
        const data = response?.data;
        const httpOk = response?.status >= 200 && response?.status < 300;
        let ok = false;
        if (httpOk) {
          if (data == null || data === "") ok = true;
          else if (typeof data === "object") {
            if (isApiSuccess(data)) ok = true;
            else if (data.success === false || data.status === "error") ok = false;
            else if (data.error != null && data.error !== "") ok = false;
            else ok = true;
          } else ok = true;
        }

        if (!ok) {
          setNewMessage(messageText);
          return;
        }

        const savedMessage = data?.message || data?.data?.message || data?.data || null;
        const savedId =
          messageDocId(savedMessage) ||
          messageDocId({ _id: data?.messageId || data?.id }) ||
          "";
        console.log("[chat] group send response", data);
        setGroupSummaries((prev) => ({
          ...prev,
          [selectedGroupType]: {
            text:
              messageText.length > 72 ? `${messageText.slice(0, 72)}…` : messageText,
            at: new Date().toISOString(),
            messageId: savedId || null
          }
        }));
        setMessages((prev) => [
          ...prev,
          {
            ...(savedMessage && typeof savedMessage === "object" ? savedMessage : {}),
            _id: savedId || `broadcast-local-${Date.now()}`,
            content: savedMessage?.content || messageText,
            senderId: savedMessage?.senderId || { _id: userId },
            createdAt: savedMessage?.createdAt || new Date().toISOString(),
            messageType: "text",
            group: selectedGroupType,
            isBroadcastEcho: !savedId
          }
        ]);
        const groupSocketPayload = {
          ...wsIdentity,
          action: "send_group_message",
          group: selectedGroupType,
          userid: userId,
          userId,
          id: userId,
          content: messageText,
          messageType: "text"
        };
        const socketReady = await ensureWsConnected();
        console.log("[chat] send group", groupSocketPayload, {
          socketReady,
          socket: getWsDebugSnapshot()
        });
        const groupSent = socketReady && sendWs(groupSocketPayload);
        if (!groupSent) {
          console.warn("[chat] group socket send failed", getWsDebugSnapshot());
          toast.warning("Message saved. The live socket is not connected, so the owner will not see it yet.");
        }
        if (!savedId) fetchGroupMessages(selectedGroupType);
      } catch (error) {
        setNewMessage(messageText);
        toast.error(
          error?.response?.data?.message || error?.message || "Failed to send group message"
        );
      } finally {
        isSendingRef.current = false;
      }
      return;
    }

    const restoreInput = () => {
      setNewMessage(messageText);
      setPendingAttachments(
        attachmentSnapshot.map((p) => ({
          id: p.id,
          file: p.file,
          previewUrl: p.file.type.startsWith("image/")
            ? URL.createObjectURL(p.file)
            : null
        }))
      );
    };

    setNewMessage("");
    setPendingAttachments([]);

    setSendingAttachments(true);
    try {
      let chatId = getChatId(selectedChat);

      if (!chatId) {
        const targetTenantId =
          selectedChat?.otherParticipant?._id || selectedChat?.userId || null;
        if (targetTenantId) {
          setLoading(true);
          const created = await createOrGetChat(targetTenantId, {
            ...(selectedChat.otherParticipant || {}),
            _id: targetTenantId,
            directoryRole: selectedChat.directoryRole
          });
          setLoading(false);
          chatId = created?.chatId || created?._id || null;
          if (!chatId) {
            restoreInput();
            return;
          }
        }
      }

      if (!chatId) {
        toast.error("Chat not ready. Please select the user again.");
        restoreInput();
        return;
      }

      const sendTextOnly = async () => {
        const res = await postChatMessage(chatId, {
          messageType: "text",
          content: messageText
        });
        if (!res.ok) {
          toast.error(res.message || "Failed to send message");
          restoreInput();
        }
      };

      if (attachmentSnapshot.length === 0) {
        await sendTextOnly();
        return;
      }

      for (let i = 0; i < attachmentSnapshot.length; i++) {
        const { file } = attachmentSnapshot[i];
        const isImage = file.type.startsWith("image/");
        const captionForFirst = i === 0 && messageText ? messageText : "";
        const mime = file.type || "application/octet-stream";

        const uploaded = await uploadChatFileViaPresign(
          API_BASE_URL,
          userId,
          chatId,
          file
        );
        if (!uploaded.s3Key) {
          toast.error(
            uploaded.error || `Could not upload "${file.name}". Check chat.getAttachmentUploadUrl and S3 CORS.`
          );
          restoreInput();
          return;
        }

        const res = await postChatMessage(chatId, {
          messageType: isImage ? "image" : "file",
          ...(captionForFirst ? { content: captionForFirst } : {}),
          fileData: {
            s3Key: uploaded.s3Key,
            fileName: file.name,
            fileSize: file.size,
            mimeType: mime
          }
        });
        if (!res.ok) {
          toast.error(res.message || "Failed to send attachment");
          restoreInput();
          return;
        }
      }

      attachmentSnapshot.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";
      toast.error(msg);
      restoreInput();
    } finally {
      setSendingAttachments(false);
      isSendingRef.current = false;
    }
  }, [
    newMessage,
    pendingAttachments,
    selectedChat,
    API_BASE_URL,
    userId,
    userRole,
    isAdminCapable,
    isActiveExecutive,
    ensureWsConnected,
    createOrGetChat,
    getChatId,
    postChatMessage,
    wsIdentity,
    fetchGroupMessages
  ]);

  // Handle typing indicators
  const isTypingRef = useRef(false);
  const typingChatIdRef = useRef(null);

  const handleTyping = () => {
    if (selectedChat?.isGroup || selectedChat?.groupType || selectedChat?.isBroadcastAllTenants) return;
    const chatId = getChatId(selectedChat);
    if (!chatId) return;

    // Only send typing_start once per typing burst
    if (!isTypingRef.current || typingChatIdRef.current !== chatId) {
      isTypingRef.current = true;
      typingChatIdRef.current = chatId;
      sendWs({ action: "typing_start", chatId, ...wsIdentity });
    }

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    typingTimeoutRef.current = setTimeout(() => {
      sendWs({ action: "typing_stop", chatId, ...wsIdentity });
      isTypingRef.current = false;
      typingChatIdRef.current = null;
    }, 1000);
  };

  const deleteChatMessage = async (message) => {
    const messageId = messageDocId(message);
    const chatId = getChatId(selectedChatRef.current);
    console.log("[chat] delete private click", { messageId, chatId, message });
    if (!API_BASE_URL || !userId || !messageId || !chatId) {
      toast.error("This message is not ready to delete yet.");
      return;
    }
    const deletedBy = isAdminCapable ? "admin" : "user";
    const payload = {
      action: "chat.deleteMessage",
      userid: userId,
      chatId,
      messageId
    };
    try {
      console.log("[chat] delete private api", payload);
      const response = await axios.post(API_BASE_URL, payload);
      const data = response?.data;
      console.log("[chat] delete private response", data);
      if (!isApiSuccess(data)) {
        toast.error(data?.message || data?.error || "Failed to delete message");
        return;
      }
      applyPrivateDelete(chatId, messageId, deletedBy, message);
      const socketPayload = {
        action: "delete_message",
        type: "message_deleted",
        chatId,
        messageId,
        status: "inactive",
        deletedBy,
        ...(deletedBy === "admin" ? { content: DELETED_GROUP_MESSAGE_TEXT } : {}),
        userid: userId,
        userId,
        id: userId
      };
      const socketReady = await ensureWsConnected();
      const sent = socketReady && sendWs(socketPayload);
      console.log("[chat] delete private socket", { socketReady, sent, socketPayload });
      if (!sent) toast.warning("Message deleted, but the live update was not sent.");
    } catch (error) {
      console.log("[chat] delete private failed", error?.response?.data || error?.message || error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete message");
    }
  };

  const resolveGroupMessageId = useCallback(
    async (message, groupType) => {
      const currentId = messageDocId(message);
      if (currentId && !currentId.startsWith("broadcast-local-")) return currentId;
      if (!API_BASE_URL || !userId || !groupType) return "";
      const response = await axios.post(API_BASE_URL, {
        action: "chat.getGroupMessages",
        userid: userId,
        group: groupType,
        page: 1,
        limit: messageLimit
      });
      const data = response?.data;
      const msgs = Array.isArray(data?.messages) ? data.messages : [];
      const content = String(message?.content || "").trim();
      const active = msgs.filter(
        (item) => String(item?.status || "active").trim().toLowerCase() !== "inactive"
      );
      const senderMatches = (item) => {
        const sender = item?.senderId?._id || item?.senderId?.id || item?.senderId || "";
        return String(sender) === String(userId);
      };
      const textMatches = (item) => String(item?.content || "").trim() === content;
      const match =
        [...active].reverse().find((item) => textMatches(item) && senderMatches(item)) ||
        [...active].reverse().find((item) => textMatches(item));
      const foundId = messageDocId(match);
      console.log("[chat] resolve group message id", { currentId, foundId, count: msgs.length });
      return foundId;
    },
    [API_BASE_URL, userId, messageLimit]
  );

  const deleteGroupMessage = useCallback(
    async (message) => {
      const selected = selectedChatRef.current;
      const groupType =
        selected?.groupType || (selected?.isBroadcastAllTenants ? GROUP_TENANTS : "");
      const localId = messageDocId(message);
      if (!API_BASE_URL || !userId || !groupType) {
        toast.error("Could not find this message to delete.");
        return;
      }
      try {
        const messageId = await resolveGroupMessageId(message, groupType);
        console.log("[chat] delete group click", { messageId, groupType, message });
        if (!messageId) {
          toast.error("Could not find this message to delete.");
          return;
        }

        const payload = {
          action: "admin.deleteGroupMessage",
          userid: userId,
          messageId,
          group: groupType
        };
        console.log("[chat] delete group api", payload);
        const response = await axios.post(API_BASE_URL, payload);
        const data = response?.data;
        console.log("[chat] delete group response", data);
        if (!isGroupDeleteSaved(data)) {
          toast.error(data?.message || data?.error || "Failed to delete message");
          return;
        }

        const savedId = messageDocId({ messageId: data?.messageId }) || messageId;
        deletedGroupMessageIdsRef.current.add(`${groupType}:${savedId}`);
        const clickedContent = String(message?.content || "").trim();
        setMessages((prev) => {
          let replaced = false;
          const next = prev.map((item) => {
            if (sameMessageId(item, savedId) || (localId && messageDocId(item) === localId)) {
              replaced = true;
              return asDeletedGroupMessage({ ...item, _id: savedId });
            }
            return item;
          });
          if (!replaced && clickedContent) {
            const reverseIndex = [...next]
              .reverse()
              .findIndex(
                (item) =>
                  String(item?.content || "").trim() === clickedContent &&
                  !isDeletedGroupMessage(item)
              );
            if (reverseIndex >= 0) {
              const index = next.length - 1 - reverseIndex;
              next[index] = asDeletedGroupMessage({ ...next[index], _id: savedId });
            }
          }
          const last = next[next.length - 1];
          if (last) applyGroupPreview(groupType, last);
          return next;
        });

        const socketPayload = {
          action: "delete_group_message",
          type: "group_message_updated",
          group: groupType,
          messageId: savedId,
          status: "inactive",
          deletedBy: "admin",
          content: DELETED_GROUP_MESSAGE_TEXT,
          userid: userId,
          userId,
          message: {
            _id: savedId,
            messageId: savedId,
            group: groupType,
            content: DELETED_GROUP_MESSAGE_TEXT,
            status: "inactive",
            deletedBy: "admin",
            messageType: "deleted"
          }
        };
        const socketReady = await ensureWsConnected();
        const sent = socketReady && sendWs(socketPayload);
        console.log("[chat] delete group socket", { socketReady, sent, socketPayload });
      } catch (error) {
        console.log("[chat] delete group failed", error?.response?.data || error?.message || error);
        toast.error(
          error?.response?.data?.message || error?.message || "Failed to delete message"
        );
      }
    },
    [API_BASE_URL, userId, applyGroupPreview, ensureWsConnected, resolveGroupMessageId]
  );

  useEffect(() => {
    if (!messageMenuId) return undefined;
    const closeMenu = (event) => {
      const target = event.target;
      if (target && target.closest && target.closest(".message-action-menu, .message-menu-btn")) {
        return;
      }
      setMessageMenuId(null);
    };
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, [messageMenuId]);
  const loadMoreChats = () => {
    // Socket-only mode: pagination depends on backend WS support
    // Keep button hidden unless backend provides pagination.
  };

  // Initial load + refetch whenever we navigate to Communication (e.g. from Header notification).
  useEffect(() => {
    if (!userId || !WS_URL || !API_BASE_URL) return;
    setChatPage(1);
    fetchChats(1, false);
  }, [userId, userRole, WS_URL, API_BASE_URL, fetchChats, location.key]);

  useEffect(() => {
    if (!userId || !API_BASE_URL) return;
    const groups = visibleGroupTypes({
      isAdmin: isAdminCapable,
      isOwner: userRole === "OWNER",
      isTenant: userRole === "TENANT",
      isActiveExecutive
    });
    groups.forEach((groupType) => {
      fetchGroupMessages(groupType, { silent: true });
    });
  }, [userId, userRole, isAdminCapable, isActiveExecutive, API_BASE_URL, fetchGroupMessages]);

  // Collapse duplicate conversation rows (same chat id and/or same counterpart) already in state.
  useEffect(() => {
    if (chats.length < 2) return;
    const cleaned = dedupeSidebarChats(chats);
    if (cleaned.length < chats.length) setChats(cleaned);
  }, [chats]);

  // Open the right thread when navigating from notifications (same route, chats already loaded).
  useEffect(() => {
    let qp;
    try {
      qp = new URLSearchParams(location.search);
    } catch {
      qp = new URLSearchParams();
    }
    const focusGroup = String(location.state?.focusGroup || qp.get("group") || "")
      .trim()
      .toUpperCase();
    if (focusGroup && GROUP_META[focusGroup]) {
      handleChatSelect({
        isGroup: true,
        groupType: focusGroup,
        isBroadcastAllTenants: focusGroup === GROUP_TENANTS,
        _id: GROUP_META[focusGroup].id,
        chatId: null,
        otherParticipant: {
          name: GROUP_META[focusGroup].name,
          email: GROUP_META[focusGroup].subtitle
        }
      });
      navigate({ pathname: "/communication", search: "" }, { replace: true, state: {} });
      return;
    }

    const tChat = location.state?.focusChatId || qp.get("chatId");
    const tPart =
      location.state?.focusParticipantId ||
      qp.get("participantId") ||
      qp.get("userId");
    if (!tChat && !tPart) return;
    if (!userId || !WS_URL || !API_BASE_URL) return;
    if (loading || chats.length === 0) return;

    const byChat =
      tChat && chats.find((c) => String(getChatId(c) || "") === String(tChat));
    const byPart =
      !byChat &&
      tPart &&
      chats.find(
        (c) =>
          String(c?.otherParticipant?._id || c?.userId || c?.participantId || "") ===
          String(tPart)
      );
    const match = byChat || byPart;

    let cancelled = false;
    (async () => {
      if (match) {
        const cid = getChatId(match);
        if (cid) localStorage.setItem(WS_ACTIVE_CHAT_ID_KEY, String(cid));
        await handleChatSelect(match);
      }
      if (!cancelled) {
        navigate(
          { pathname: "/communication", search: "" },
          { replace: true, state: {} }
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    location.key,
    location.state,
    location.search,
    chats,
    loading,
    userId,
    WS_URL,
    API_BASE_URL,
    getChatId,
    handleChatSelect,
    navigate
  ]);

  // Format message time
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getChatDisplayName = (chat) => {
    const groupType = chat?.groupType || (chat?.isBroadcastAllTenants ? GROUP_TENANTS : "");
    if (groupType && GROUP_META[groupType]) return GROUP_META[groupType].name;
    const p = chat?.otherParticipant;
    if (!p) return "Unknown User";
    return p.name || p.email || "Unknown User";
  };

  const getLastMessagePreview = (chat) => {
    const groupType = chat?.groupType || (chat?.isBroadcastAllTenants ? GROUP_TENANTS : "");
    if (groupType && GROUP_META[groupType] && !chat?.lastMessage) {
      return GROUP_META[groupType].subtitle;
    }
    const roleLabel = DIRECTORY_ROLE_LABEL[chat?.directoryRole];
    const lm = chat?.lastMessage;
    const content = String(lm?.content || "").trim();
    if (roleLabel && !content) return roleLabel;
    if (!lm) return roleLabel || "No messages yet";
    const mt = String(lm.messageType || "text").toLowerCase();
    if (mt === "image") return "Photo";
    if (mt === "file" || mt === "document") {
      return lm.fileName ? `File: ${lm.fileName}` : "Document";
    }
    if (!content) return roleLabel || "Message";
    return content.length > 50 ? content.substring(0, 50) + "..." : content;
  };

  /** Stable React keys for sidebar rows — never use random values (causes remount/flicker). */
  const getSidebarListRowKey = (chat, listIndex) => {
    const groupType = chat?.groupType || (chat?.isBroadcastAllTenants ? GROUP_TENANTS : "");
    if (groupType && GROUP_META[groupType]) return GROUP_META[groupType].id;
    const cid = getChatId(chat);
    if (cid) return `conv-${String(cid)}`;
    const p = chat?.otherParticipant;
    const peerId = p?._id ?? p?.id ?? chat?.userId ?? chat?.participantId;
    if (peerId != null && String(peerId) !== "") return `peer-${String(peerId)}`;
    const em = String(p?.email || "").trim().toLowerCase();
    const nm = String(getChatDisplayName(chat) || "").trim().toLowerCase();
    const label = `${em}::${nm}`;
    if (label !== "::") return `label-${label}`;
    return `conv-idx-${listIndex}`;
  };

  const saveBlobToDevice = useCallback(async (blob, filename) => {
    if (!(blob instanceof Blob) || blob.size < 1) return false;

    const picker = window.showSaveFilePicker;
    if (typeof picker === "function") {
      try {
        const handle = await picker({ suggestedName: filename });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.success("Saved to your device");
        return true;
      } catch (e) {
        if (e?.name === "AbortError") return true;
      }
    }

    const obj = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = obj;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(obj), 5000);
    toast.success("Download started");
    return true;
  }, []);

  const tryFetchAttachmentBlob = useCallback(async (url) => {
    const res = await fetch(url, { mode: "cors", credentials: "omit", cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  }, []);

  /** Same-origin download when S3 blocks CORS — implement `chat.getAttachmentFile` (response: raw bytes / blob). */
  const tryRelayAttachmentBlob = useCallback(
    async (relay) => {
      const { s3Key, chatId, messageId } = relay || {};
      if (!API_BASE_URL || !userId || !s3Key) return null;
      try {
        const res = await axios.post(
          API_BASE_URL,
          {
            action: "chat.getAttachmentFile",
            userid: userId,
            s3Key,
            ...(chatId ? { chatId } : {}),
            ...(messageId ? { messageId } : {})
          },
          { responseType: "blob", validateStatus: (s) => s === 200 }
        );
        const blob = res?.data;
        if (!(blob instanceof Blob) || blob.size < 1) return null;
        if (blob.type && blob.type.includes("application/json")) return null;
        return blob;
      } catch {
        return null;
      }
    },
    [API_BASE_URL, userId]
  );

  const downloadAttachmentAsFile = useCallback(
    async (url, name, mimeHint, relay) => {
      if (!url && !(relay?.s3Key && API_BASE_URL)) {
        toast.error("No download link available.");
        return;
      }
      const mime = String(mimeHint || "").toLowerCase();
      let filename = name || "image";
      if (!/\.[a-z0-9]{2,8}$/i.test(filename)) {
        const ext =
          mime.includes("png") ? ".png"
          : mime.includes("webp") ? ".webp"
          : mime.includes("gif") ? ".gif"
          : mime.includes("svg") ? ".svg"
          : ".jpg";
        filename = `${filename.replace(/\.+$/g, "")}${ext}`;
      }

      if (url && url.startsWith("data:")) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Download started");
        return;
      }

      let blob = null;
      if (url) {
        try {
          blob = await tryFetchAttachmentBlob(url);
        } catch {
          blob = null;
        }
      }
      if (!blob) {
        blob = await tryRelayAttachmentBlob(relay);
      }

      if (blob) {
        await saveBlobToDevice(blob, filename);
        return;
      }

      toast.error(
        "Could not save the file. Enable CORS on the S3 bucket for this site, or add API action chat.getAttachmentFile (same-origin blob) so downloads work without opening a new tab."
      );
    },
    [API_BASE_URL, tryFetchAttachmentBlob, tryRelayAttachmentBlob, saveBlobToDevice]
  );

  const renderImageWithWhatsappMenu = (message, src) => {
    const menuKey =
      message?._id != null && message._id !== ""
        ? String(message._id)
        : `img-${String(src).slice(0, 80)}`;
    const fname = message?.fileName || "image";
    const downloadUrl = getMessageDisplayUrl(message) || src;
    const isOpen = openImageMenuMessageId === menuKey;

    return (
      <div className="chat-msg-image-wrap">
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-msg-image-link"
        >
          <img src={src} alt="" className="chat-msg-attachment-img" loading="lazy" />
        </a>
        <div className="chat-msg-image-menu-host">
          <button
            type="button"
            className="chat-msg-img-menu-trigger"
            aria-expanded={isOpen}
            aria-haspopup="true"
            aria-label="Image options"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpenImageMenuMessageId((id) => (id === menuKey ? null : menuKey));
            }}
          >
            <i className="fa-solid fa-chevron-down" aria-hidden />
          </button>
          {isOpen && (
            <div className="chat-msg-img-menu" role="menu">
              <button
                type="button"
                className="chat-msg-img-menu-item"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenImageMenuMessageId(null);
                  downloadAttachmentAsFile(
                    downloadUrl,
                    fname,
                    message?.mimeType || message?.contentType,
                    {
                      s3Key: message?.s3Key,
                      chatId: message?.chatId,
                      messageId: message?._id
                    }
                  );
                }}
              >
                <i className="fa-solid fa-download" aria-hidden />
                <span>Save as</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMessageBody = (message) => {
    const mt = String(message.messageType || "text").toLowerCase();
    const url = getMessageDisplayUrl(message);
    const fname = message.fileName || "Attachment";
    const content = message.content;
    const mime = String(message.mimeType || message.contentType || "").toLowerCase();

    const attachmentFallback = (kindLabel) => (
      <div className="chat-msg-attachment-fallback small">
        <div className="fw-semibold text-body">{fname}</div>
        <span className="text-muted">
          {kindLabel} link missing or expired. Reload this chat to refresh presigned URLs.
        </span>
      </div>
    );

    if (mt === "image" || (mime.startsWith("image/") && message.s3Key)) {
      const src =
        url ||
        (typeof content === "string" && content.startsWith("data:image") ? content : null);
      if (src) {
        return (
          <>
            {renderImageWithWhatsappMenu(message, src)}
            {content &&
              content !== src &&
              typeof content === "string" &&
              !content.startsWith("data:") && (
                <div className="chat-msg-caption mt-1">{content}</div>
              )}
          </>
        );
      }
      return attachmentFallback("Image");
    }

    if (mt === "file" || mt === "document") {
      const href =
        url ||
        (typeof content === "string" &&
        (content.startsWith("http://") || content.startsWith("https://"))
          ? content
          : null);
      if (href) {
        return (
          <>
            <a
              href={href}
              download={fname}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-msg-file-link"
            >
              <i className="fa-solid fa-paperclip me-1" aria-hidden />
              {fname}
            </a>
            {content &&
              content !== href &&
              typeof content === "string" &&
              !content.startsWith("http") && (
                <div className="chat-msg-caption mt-1 small">{content}</div>
              )}
          </>
        );
      }
      return attachmentFallback("File");
    }

    if (typeof content === "string" && content.startsWith("data:image")) {
      return renderImageWithWhatsappMenu(message, content);
    }

    return <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{content}</span>;
  };

  const isUserOnline = (chat) => {
    const id = chat?.otherParticipant?._id;
    return id ? onlineUsers.has(id) : false;
  };

  const roleFlags = {
    isAdmin: isAdminCapable,
    isOwner: userRole === "OWNER",
    isTenant: userRole === "TENANT",
    isActiveExecutive
  };
  const groupListItems = visibleGroupTypes(roleFlags).map((groupType) => {
    const meta = GROUP_META[groupType];
    const summary = groupSummaries[groupType];
    return {
      isGroup: true,
      groupType,
      isBroadcastAllTenants: groupType === GROUP_TENANTS,
      _id: meta.id,
      chatId: null,
      otherParticipant: {
        name: meta.name,
        email: meta.subtitle
      },
      lastMessage: summary
        ? {
            content: summary.text,
            timestamp: summary.at,
            messageType: "text"
          }
        : null
    };
  });

  // Groups stay pinned. Admin and executive names stay above other people.
  const filteredTenantChats = chats.filter((chat) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const displayName = (getChatDisplayName(chat) || "").toLowerCase();
    const lastPreview = (getLastMessagePreview(chat) || "").toLowerCase();
    const email = (chat.otherParticipant?.email || "").toLowerCase();

    return displayName.includes(q) || lastPreview.includes(q) || email.includes(q);
  });

  const sortedFilteredTenantChats = [...filteredTenantChats].sort((a, b) => {
    const timeDiff = conversationActivityMs(b) - conversationActivityMs(a);
    if (timeDiff !== 0) return timeDiff;

    const safeUnread = (c) => {
      const v = Number(getUnreadForMe(c));
      return Number.isFinite(v) && v > 0 ? v : 0;
    };
    const unreadDiff = safeUnread(b) - safeUnread(a);
    if (unreadDiff !== 0) return unreadDiff;

    return conversationSortTieBreakName(a, b);
  });

  const directoryOrder = { ADMIN: 0, SUPERADMIN: 0, EXECUTIVE: 1, TENANT: 2, OWNER: 3 };
  const conversationRows = sortedFilteredTenantChats.filter((chat) => {
    if (!isAdminCapable) return true;
    return Boolean(getChatId(chat));
  });
  const priorityPeople = [];
  const otherPeople = [];
  conversationRows.forEach((chat) => {
    if (directoryOrder[chat.directoryRole] != null) {
      priorityPeople.push(chat);
    } else {
      otherPeople.push(chat);
    }
  });
  priorityPeople.sort((a, b) => {
    const orderDiff = directoryOrder[a.directoryRole] - directoryOrder[b.directoryRole];
    if (orderDiff !== 0) return orderDiff;
    return conversationSortTieBreakName(a, b);
  });
  const visibleGroups = groupListItems.filter((chat) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      getChatDisplayName(chat).toLowerCase().includes(q) ||
      String(chat.otherParticipant?.email || "")
        .toLowerCase()
        .includes(q)
    );
  });
  const filteredChats = [...visibleGroups, ...priorityPeople, ...otherPeople];
  const pickerNeedle = pickerQuery.trim().toLowerCase();
  const pickerPeople = directoryPeople
    .filter((person) => {
      if (!pickerNeedle) return true;
      const name = getChatDisplayName(person).toLowerCase();
      const email = String(person.otherParticipant?.email || "").toLowerCase();
      const role = String(DIRECTORY_ROLE_LABEL[person.directoryRole] || "").toLowerCase();
      return name.includes(pickerNeedle) || email.includes(pickerNeedle) || role.includes(pickerNeedle);
    })
    .sort((a, b) => {
      const order = { OWNER: 0, TENANT: 1, EXECUTIVE: 2, ADMIN: 3 };
      const diff = (order[a.directoryRole] ?? 9) - (order[b.directoryRole] ?? 9);
      if (diff !== 0) return diff;
      return getChatDisplayName(a).localeCompare(getChatDisplayName(b));
    });
  const selectedGroupType =
    selectedChat?.groupType || (selectedChat?.isBroadcastAllTenants ? GROUP_TENANTS : "");
  const selectedIsGroup = Boolean(selectedGroupType);
  const canSendToSelectedGroup =
    !selectedIsGroup || canPostToGroup(selectedGroupType, roleFlags);

  const mobileThreadMode = isMobileChat && mobileThreadOpen;

  return (
    <div
      className={`employee-form communication-page${
        mobileThreadMode ? " communication-mobile-thread-open" : ""
      }`}
    >
      <div
        className={`content-header${
          mobileThreadMode ? " communication-mobile-page-header-hidden" : ""
        }`}
      >
        <div className="container-fluid">
          <h1 className="m-0 txt_lft">Communication</h1>
          {/* <div className="d-flex align-items-center gap-3">        
            {!wsConnected && (
              <button 
                className="btn btn-sm btn-warning"
                onClick={() => {
                  reconnectWs().then((ok) => {
                    if (ok) toast.success("WebSocket reconnected");
                  });
                }}
                title="Reconnect WebSocket"
              >
                <i className="fa-solid fa-plug"></i>
              </button>
            )}
          </div> */}
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {!userId || !WS_URL || !API_BASE_URL ? (
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center p-5">
                    <i className="fa-solid fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                    <h5>Configuration Error</h5>
                    <p className="text-muted">
                      {!userId && "User not logged in. Please login to access chat."}
                      {!WS_URL && "WebSocket URL not configured. Please check environment settings."}
                      {!API_BASE_URL && "API URL not configured (needed to load chat users list)."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="row communication-layout">
              {/* Left Panel - Chat List */}
              <div className="col-12 col-lg-4 communication-sidebar">
                <div className="card h-100">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong className="d-lg-none">Chats</strong>
                      <strong className="d-none d-lg-inline">Conversations</strong>
                      {isAdminCapable ? (
                        <button
                          type="button"
                          className="btn btn-link chat-new-btn p-0"
                          aria-label="New individual chat"
                          title="New individual chat"
                          onClick={() => {
                            setPickerQuery("");
                            setPeoplePickerOpen(true);
                          }}
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true"></i>
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="card-body p-0">
                    {/* Search */}
                    <div className="chat-search-wrapper">
                      <input
                        type="text"
                        className="form-control chat-search"
                        placeholder="Search conversations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>

                    {/* Chat List */}
                    <div className="chat-list">
                      {loading && chats.length === 0 ? (
                        <div className="text-center p-4">
                          <div className="text-muted">Loading conversations...</div>
                        </div>
                      ) : filteredChats.length > 0 ? (
                        <>
                          {filteredChats.map((chat, index) => {
                            const key = getSidebarListRowKey(chat, index);

                            const isSelected = isConversationSelected(chat);

                            const isGroupRow = Boolean(chat.isGroup || chat.groupType || chat.isBroadcastAllTenants);
                            const unread = isGroupRow ? 0 : getUnreadForMe(chat);
                            const hasUnread = !isGroupRow && Number(unread) > 0;

                            return (
                              <div
                                key={key}
                                role="button"
                                tabIndex={0}
                                aria-current={isSelected ? "true" : undefined}
                                aria-label={`${getChatDisplayName(chat)}${
                                  hasUnread ? `, ${unread} unread messages` : ", read"
                                }`}
                                className={`chat-item ${isGroupRow ? "chat-item-broadcast" : ""} ${
                                  isGroupRow ? "" : hasUnread ? "chat-item-unread" : "chat-item-read"
                                } ${isSelected ? "active" : ""}`}
                                onClick={() => handleChatSelect(chat)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleChatSelect(chat);
                                  }
                                }}
                              >
                                <div className="chat-avatar">
                                  {isGroupRow ? (
                                    <div className="avatar-circle broadcast-all-avatar" title="Group message">
                                      <i className="fa-solid fa-users" aria-hidden />
                                    </div>
                                  ) : (
                                    <>
                                      <div className="avatar-circle">
                                        {getChatDisplayName(chat).charAt(0).toUpperCase()}
                                      </div>
                                      {isUserOnline(chat) && (
                                        <div className="online-indicator"></div>
                                      )}
                                    </>
                                  )}
                                </div>

                                <div className="chat-info">
                                  <div className="chat-name chat-name-row">
                                    {hasUnread && (
                                      <span
                                        className="chat-unread-indicator"
                                        title="Unread messages"
                                        aria-hidden
                                      />
                                    )}
                                    <span className="chat-name-text">{getChatDisplayName(chat)}</span>
                                  </div>
                                  <div
                                    className={`chat-preview ${
                                      hasUnread ? "chat-preview-unread" : "chat-preview-read"
                                    }`}
                                  >
                                    {getLastMessagePreview(chat)}
                                  </div>
                                </div>

                                <div className="chat-meta">
                                  <div className="chat-time">
                                    {chat.lastMessage?.timestamp &&
                                      formatMessageTime(chat.lastMessage.timestamp)}
                                  </div>
                                  {unread > 0 && <div className="unread-badge">{unread}</div>}
                                </div>
                              </div>
                            );
                          })}

                          {/* Load More Button */}
                          {hasMoreChats && (
                            <div className="text-center p-3">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={loadMoreChats}
                                disabled={loading}
                              >
                                {loading ? "Loading..." : "Load More"}
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="communication-error">
                          <i className="fa-solid fa-comments"></i>
                          <div className="text-muted">
                            {searchQuery
                              ? "No conversations found"
                              : userRole === "ADMIN" || userRole === "SUPERADMIN"
                              ? "No individual chats yet. Use + to message someone."
                              : "No people to message yet."}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {isAdminCapable && peoplePickerOpen ? (
                    <div className="chat-picker-layer" role="dialog" aria-label="Choose a person">
                      <div className="chat-picker-panel">
                        <div className="chat-picker-header">
                          <strong>New message</strong>
                          <button
                            type="button"
                            className="btn btn-link p-0 chat-picker-close"
                            aria-label="Close"
                            onClick={() => setPeoplePickerOpen(false)}
                          >
                            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                          </button>
                        </div>
                        <div className="chat-search-wrapper">
                          <input
                            type="text"
                            className="form-control chat-search"
                            placeholder="Search owners, tenants, executives..."
                            value={pickerQuery}
                            onChange={(e) => setPickerQuery(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="chat-list">
                          {pickerPeople.length > 0 ? (
                            pickerPeople.map((person) => (
                              <button
                                key={`pick-${person.userId || person.participantId}`}
                                type="button"
                                className="chat-item chat-picker-item"
                                onClick={() => {
                                  setPeoplePickerOpen(false);
                                  handleChatSelect(person);
                                }}
                              >
                                <div className="chat-avatar">
                                  <div className="avatar-circle">
                                    {getChatDisplayName(person).charAt(0).toUpperCase()}
                                  </div>
                                </div>
                                <div className="chat-info">
                                  <div className="chat-name">{getChatDisplayName(person)}</div>
                                  <div className="chat-preview chat-preview-read">
                                    {DIRECTORY_ROLE_LABEL[person.directoryRole] || "Member"}
                                  </div>
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="text-center p-4 text-muted">No people found</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Right Panel - Chat Messages */}
              <div className="col-12 col-lg-8 communication-main">
                <div className="card h-100">
                  {selectedChat ? (
                    <>
                      {/* Chat Header */}
                      <div className="card-header chat-header">
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div className="d-flex align-items-center min-w-0 flex-grow-1">
                            {isMobileChat && (
                              <button
                                type="button"
                                className="btn btn-link chat-mobile-back p-0 me-2 flex-shrink-0"
                                aria-label="Back to chats"
                                onClick={() => setMobileThreadOpen(false)}
                              >
                                <i className="fa-solid fa-arrow-left fa-lg" aria-hidden />
                              </button>
                            )}
                            <div className="chat-avatar me-3 flex-shrink-0">
                              {selectedChat.isGroup || selectedChat.groupType || selectedChat.isBroadcastAllTenants ? (
                                <div className="avatar-circle broadcast-all-avatar broadcast-all-header-avatar">
                                  <i className="fa-solid fa-users" aria-hidden />
                                </div>
                              ) : (
                                <>
                                  <div className="avatar-circle">
                                    {getChatDisplayName(selectedChat).charAt(0).toUpperCase()}
                                  </div>
                                  {isUserOnline(selectedChat) && (
                                    <div className="online-indicator"></div>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="min-w-0">
                              <strong className="d-block text-truncate">
                                {getChatDisplayName(selectedChat)}
                              </strong>
                              <div className="text-muted small text-truncate">
                                {selectedChat.groupType && GROUP_META[selectedChat.groupType]
                                  ? GROUP_META[selectedChat.groupType].subtitle
                                  : selectedChat.isBroadcastAllTenants
                                  ? GROUP_META[GROUP_TENANTS].subtitle
                                  : selectedChat.directoryRole &&
                                    DIRECTORY_ROLE_LABEL[selectedChat.directoryRole]
                                  ? DIRECTORY_ROLE_LABEL[selectedChat.directoryRole]
                                  : displayEmail(selectedChat.otherParticipant?.email)}
                              </div>
                              {typingUsers.length > 0 &&
                                !selectedChat.isGroup &&
                                !selectedChat.groupType &&
                                !selectedChat.isBroadcastAllTenants && (
                                <div className="typing-indicator">
                                  <small className="text-muted">Typing...</small>
                                </div>
                              )}
                            </div>
                          </div>
                          {isMobileChat && !wsConnected && (
                            <button
                              type="button"
                              className="btn btn-sm btn-warning flex-shrink-0"
                              title="Reconnect"
                              onClick={() => {
                                reconnectWs().then((ok) => {
                                  if (ok) toast.success("WebSocket reconnected");
                                });
                              }}
                            >
                              <i className="fa-solid fa-plug" aria-hidden />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Messages Area */}
                      <div
                        className="card-body chat-messages"
                        ref={messagesScrollRef}
                        onScroll={updateStickToBottomFromScroll}
                      >
                        {messagesLoading ? (
                          <div className="text-center p-4">
                            <div className="text-muted">Loading messages...</div>
                          </div>
                        ) : messages.length > 0 ? (
                          <div className="messages-container">
                            {messages
                              .filter(
                                (message) =>
                                  isDeletedGroupMessage(message) ||
                                  String(message?.content || message?.fileName || "").trim() !== ""
                              )
                              .map((message, index, visibleMessages) => {
                              const sender =
                                message.senderId?._id || message.senderId || "";
                              const isOwnMessage = String(sender) === String(userId);

                              const prevSender =
                                index > 0
                                  ? visibleMessages[index - 1].senderId?._id ||
                                    visibleMessages[index - 1].senderId
                                  : null;

                              const showAvatar =
                                index === 0 || String(prevSender) !== String(sender);
                              const senderName = messageSenderName(message);
                              const senderRole = normalizeDirectoryRole(
                                message?.senderId?.role ||
                                  message?.senderId?.userType ||
                                  message?.senderRole ||
                                  ""
                              );
                              const messageDeleted = isDeletedGroupMessage(message);
                              const canDeleteGroupMessage =
                                selectedIsGroup &&
                                isAdminCapable &&
                                !messageDeleted &&
                                (isOwnMessage ||
                                  senderRole === "ADMIN" ||
                                  senderRole === "SUPERADMIN" ||
                                  senderRole === "EXECUTIVE" ||
                                  executiveSenderIds.has(String(sender)));
                              const canDeletePrivateMessage =
                                !selectedIsGroup && !messageDeleted && (isOwnMessage || isAdminCapable);
                              const canDeleteMessage = canDeleteGroupMessage || canDeletePrivateMessage;
                              const peerInitial = (
                                (selectedIsGroup ? senderName : getChatDisplayName(selectedChat)) || "U"
                              )
                                .charAt(0)
                                .toUpperCase();

                              const showSenderName =
                                selectedIsGroup && !isOwnMessage && showAvatar && senderName && !messageDeleted;
                              const incomingGroup = selectedIsGroup && !isOwnMessage;

                              return (
                                <div
                                  key={message._id}
                                  className={`message ${
                                    isOwnMessage ? "own-message" : "other-message"
                                  } ${incomingGroup ? "group-message" : ""} ${
                                    messageMenuId === message._id ? "menu-open" : ""
                                  }`}
                                >
                                  {!isOwnMessage && showAvatar && selectedIsGroup && (
                                    <div className="message-avatar">
                                      <div className="avatar-circle small">{peerInitial}</div>
                                    </div>
                                  )}
                                  {!isOwnMessage && !showAvatar && selectedIsGroup && (
                                    <div className="message-avatar message-avatar-spacer" aria-hidden />
                                  )}

                                  <div className="message-content">
                                    <div className="message-bubble-row">
                                      <div
                                        className={`message-bubble ${
                                          messageMenuId === message._id ? "menu-open" : ""
                                        }`}
                                        onDoubleClick={() => {
                                          if (!canDeleteMessage) return;
                                          if (canDeleteGroupMessage) deleteGroupMessage(message);
                                          else deleteChatMessage(message);
                                        }}
                                        title={canDeleteMessage ? "Double-click to delete" : ""}
                                      >
                                      {canDeleteMessage ? (
                                        <button
                                          type="button"
                                          className="message-menu-btn"
                                          aria-label="Message options"
                                          aria-expanded={messageMenuId === message._id}
                                          onMouseDown={(event) => event.stopPropagation()}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setMessageMenuId((current) =>
                                              current === message._id ? null : message._id
                                            );
                                          }}
                                        >
                                          <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
                                        </button>
                                      ) : null}
                                      {messageMenuId === message._id ? (
                                        <div
                                          className="message-action-menu"
                                          role="menu"
                                          onMouseDown={(event) => event.stopPropagation()}
                                        >
                                          <button
                                            type="button"
                                            className="message-action-item"
                                            role="menuitem"
                                            onMouseDown={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                            }}
                                            onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              const targetMessage = message;
                                              const removeGroup = canDeleteGroupMessage;
                                              setMessageMenuId(null);
                                              if (removeGroup) deleteGroupMessage(targetMessage);
                                              else deleteChatMessage(targetMessage);
                                            }}
                                          >
                                            <i className="fa-solid fa-trash" aria-hidden="true"></i>
                                            Delete
                                          </button>
                                        </div>
                                      ) : null}
                                      {showSenderName ? (
                                        <div
                                          className="message-sender-name"
                                          style={{ color: senderNameColor(sender) }}
                                        >
                                          {senderName}
                                        </div>
                                      ) : null}
                                      <div className="message-bubble-body">
                                        {messageDeleted ? (
                                          <span className="message-deleted-text">
                                            {DELETED_GROUP_MESSAGE_TEXT}
                                          </span>
                                        ) : (
                                          renderMessageBody(message)
                                        )}
                                        {!messageDeleted && message.isEdited && (
                                          <span className="text-muted small"> (edited)</span>
                                        )}
                                      </div>
                                      {incomingGroup ? (
                                        <span className="message-bubble-time">
                                          {formatMessageTime(message.createdAt)}
                                        </span>
                                      ) : null}
                                      {!incomingGroup && (
                                      <div className="message-bubble-time-one-one">
                                        {formatMessageTime(message.createdAt)}
                                        {isOwnMessage &&
                                          message.readBy &&
                                          message.readBy.length > 0 && (
                                            <i
                                              className="fa-solid fa-check-double ms-1 text-primary"
                                              title="Read"
                                            ></i>
                                          )}
                                      </div>
                                    )}
                                      </div>
                                    </div>
                                    {/*{!incomingGroup && (
                                      <div className="message-time">
                                        {formatMessageTime(message.createdAt)}
                                        {isOwnMessage &&
                                          message.readBy &&
                                          message.readBy.length > 0 && (
                                            <i
                                              className="fa-solid fa-check-double ms-1 text-primary"
                                              title="Read"
                                            ></i>
                                          )}
                                      </div>
                                    )} */}
                                  </div>
                                </div>
                              );
                            })}
                            <div className="chat-messages-end-spacer" aria-hidden />
                          </div>
                        ) : (
                          <div className="text-center p-4">
                            <div className="text-muted">
                              {selectedIsGroup ? (
                                <>
                                  <i className="fa-solid fa-users mb-2 d-block fs-3 text-primary" />
                                  <p className="mb-0">
                                    {selectedGroupType === GROUP_OWNERS &&
                                      "Messages here go to every owner. Owner replies stay in this group."}
                                    {selectedGroupType === GROUP_TENANTS &&
                                      "Messages here go to every tenant. Tenant replies stay in this group."}
                                    {selectedGroupType === GROUP_EXECUTIVES &&
                                      "Messages here go to every active executive member."}
                                  </p>
                                </>
                              ) : (
                                "No messages yet. Start the conversation!"
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Message Input */}
                      <div className="card-footer chat-input">
                        {selectedIsGroup && !canSendToSelectedGroup ? (
                          <div className="text-muted small mb-0">
                            You can read this group. An admin or an active executive member sends the message.
                          </div>
                        ) : null}
                        {canSendToSelectedGroup && !selectedIsGroup && (
                          <>
                            <input
                              ref={imageAttachmentInputRef}
                              type="file"
                              accept="image/*"
                              multiple
                              className="d-none"
                              onChange={(e) => {
                                addAttachmentsFromFileList(e.target.files);
                                e.target.value = "";
                              }}
                            />
                            <input
                              ref={fileAttachmentInputRef}
                              type="file"
                              accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                              multiple
                              className="d-none"
                              onChange={(e) => {
                                addAttachmentsFromFileList(e.target.files);
                                e.target.value = "";
                              }}
                            />
                          </>
                        )}
                        {!selectedIsGroup && pendingAttachments.length > 0 && (
                          <div className="chat-attachment-preview-row mb-2">
                            {pendingAttachments.map((p) => (
                              <div key={p.id} className="chat-attachment-chip">
                                {p.previewUrl ? (
                                  <img src={p.previewUrl} alt="" className="chat-attachment-thumb" />
                                ) : (
                                  <div className="chat-attachment-doc">
                                    <i className="fa-solid fa-file-lines" aria-hidden />
                                  </div>
                                )}
                                <span className="chat-attachment-name" title={p.file.name}>
                                  {p.file.name}
                                </span>
                                <button
                                  type="button"
                                  className="chat-attachment-remove"
                                  onClick={() => removePendingAttachment(p.id)}
                                  aria-label="Remove attachment"
                                >
                                  <i className="fa-solid fa-xmark" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {canSendToSelectedGroup && (
                        <div className="input-group">
                          {!selectedIsGroup && (
                            <>
                              <button
                                type="button"
                                className="btn btn-outline-secondary chat-attach-btn"
                                title="Photos"
                                disabled={sendingAttachments}
                                onClick={() => imageAttachmentInputRef.current?.click()}
                              >
                                <i className="fa-solid fa-image" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-secondary chat-attach-btn"
                                title="Documents"
                                disabled={sendingAttachments}
                                onClick={() => fileAttachmentInputRef.current?.click()}
                              >
                                <i className="fa-solid fa-paperclip" />
                              </button>
                            </>
                          )}
                          <textarea
                            ref={messageTextareaRef}
                            className="form-control"
                            placeholder={
                              selectedGroupType === GROUP_OWNERS
                                ? "Message all owners…"
                                : selectedGroupType === GROUP_TENANTS
                                ? "Message all tenants…"
                                : selectedGroupType === GROUP_EXECUTIVES
                                ? "Message executive members…"
                                : "Type a message..."
                            }
                            rows={1}
                            value={newMessage}
                            disabled={sendingAttachments}
                            onChange={(e) => {
                              setNewMessage(e.target.value);
                              handleTyping();
                              // Auto-grow up to 5 lines (~120px), then scroll
                              e.target.style.height = "auto";
                              const lineHeight = 24;
                              const maxHeight = lineHeight * 5 + 24; // 5 lines + padding
                              const newHeight = Math.min(e.target.scrollHeight, maxHeight);
                              e.target.style.height = newHeight + "px";
                              e.target.style.overflowY = e.target.scrollHeight > maxHeight ? "auto" : "hidden";
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                              }
                            }}
                            style={{ resize: "none", overflowY: "hidden", alignSelf: "center" }}
                          />
                          <button
                            className="btn btn-primary"
                            type="button"
                            onClick={sendMessage}
                            disabled={
                              sendingAttachments ||
                              (!newMessage.trim() && pendingAttachments.length === 0)
                            }
                          >
                            {sendingAttachments ? (
                              <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="fa-solid fa-paper-plane" />
                            )}
                          </button>
                        </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="card-body d-flex align-items-center justify-content-center">
                      <div className="text-center">
                        <i className="fa-solid fa-comments fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">Select a conversation to start chatting</h5>
                        <p className="text-muted">
                          Choose a conversation from the left panel to view messages
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* WebSocket Debug Panel */}
      {showDebug && (
        <div className="ws-debug" style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '5px',
          padding: '15px',
          maxWidth: '400px',
          maxHeight: '500px',
          overflow: 'auto',
          zIndex: 9999,
          fontSize: '12px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
            WebSocket Debug Panel
          </div>
          <div><strong>Status:</strong> <span style={{color: wsConnected ? 'green' : 'red'}}>{wsConnected ? 'Connected' : 'Disconnected'}</span></div>
          <div><strong>URL:</strong> {WS_URL}</div>
          <div><strong>User ID:</strong> {userId}</div>
          <div><strong>Role:</strong> {userRole}</div>
          <div><strong>Selected Chat:</strong> {selectedChat?._id || 'None'}</div>
          <div><strong>Messages Count:</strong> {messages.length}</div>
          <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
            <strong>Debug Info:</strong>
            <p style={{ fontSize: '11px', color: '#444', marginTop: '6px', lineHeight: 1.4 }}>
              Set <code style={{ fontSize: '10px' }}>localStorage.wsDebug=&quot;1&quot;</code> and reload.
              When the other user sends a chat message, watch the console for <code>[ws] recv</code>.
              If <strong>recvCount</strong> never increases, the browser is not receiving pushes — that is a
              <strong> backend</strong> issue (Lambda <code>postToConnection</code> / connection mapping), not this UI.
            </p>
            <pre style={{ fontSize: '10px', background: '#f5f5f5', padding: '5px', borderRadius: '3px', marginTop: '5px' }}>
              {JSON.stringify(getWsDebugSnapshot(), null, 2)}
            </pre>
          </div>
          <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
            <button 
              className="btn btn-sm btn-primary me-2"
              onClick={() => {
                console.log("=== MANUAL DEBUG TEST ===");
                console.log("Current state:", {
                  wsConnected,
                  selectedChat: selectedChat?._id,
                  messagesCount: messages.length,
                  userId,
                  userRole
                });
                
                if (selectedChat) {
                  const testMsg = {
                    action: "send_message",
                    chatId: getChatId(selectedChat),
                    userId,
                    content: `Debug test at ${new Date().toLocaleTimeString()}`,
                    messageType: "text"
                  };
                  console.log("Sending test message:", testMsg);
                  const result = sendWs(testMsg);
                  console.log("Send result:", result);
                  toast.info(`Debug test sent: ${result}`);
                } else {
                  toast.warning("No chat selected for debug test");
                }
              }}
            >
              Send Test
            </button>
            <button 
              className="btn btn-sm btn-warning me-2"
              onClick={() => {
                console.log("=== SIMULATING INCOMING MESSAGE ===");
                if (selectedChat) {
                  const simulatedMessage = {
                    type: "new_message",
                    chatId: getChatId(selectedChat),
                    message: {
                      _id: "sim-" + Date.now(),
                      content: `✅ SIMULATED: Server would broadcast this message at ${new Date().toLocaleTimeString()}`,
                      senderId: { _id: "other-user", name: "Test User" },
                      createdAt: new Date().toISOString(),
                      messageType: "text"
                    }
                  };
                  console.log("Simulating incoming message:", simulatedMessage);
                  handleNewMessage(simulatedMessage);
                  toast.success("✅ Frontend works! This proves client-side message handling is perfect.");
                } else {
                  toast.warning("No chat selected");
                }
              }}
            >
              🧪 Test Frontend
            </button>
            <button 
              className="btn btn-sm btn-danger me-2"
              onClick={() => {
                console.log("=== SIMULATING GROUP MESSAGE DELETE ===");
                const simulatedDelete = {
                  action: "delete_group_message",
                  type: "group_message_updated",
                  group: "OWNERS",
                  messageId: "test-message-id",
                  status: "inactive",
                  deletedBy: "admin",
                  messageType: "deleted",
                  timestamp: new Date().toISOString()
                };
                console.log("Simulating group delete message:", simulatedDelete);
                handleNewMessage(simulatedDelete);
                toast.success("✅ Group deletion simulation complete!");
              }}
            >
              🗑️ Test Group Delete
            </button>
            <button 
              className="btn btn-sm btn-secondary"
              onClick={() => {
                console.log("=== WEBSOCKET RECONNECT ===");
                reconnectWs()
                  .then((ok) => {
                    if (ok) {
                      console.log("Reconnect successful");
                      toast.success("WebSocket reconnected");
                    } else {
                      toast.error("Reconnect failed");
                    }
                  })
                  .catch((error) => {
                    console.error("Reconnect failed:", error);
                    toast.error("Reconnect failed");
                  });
              }}
            >
              Reconnect
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Communication;